import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import { finishTrip, clearHold, getOcMessages, sendOcMessage, uploadRateCon, setRcInstruction, fetchStoredDoc, fetchMergedDocs, fetchRundownPdf, markOutsideCarrier, logCheckin, createDriverLink, textDriverLink, markDriverLinkSent, revokeDriverLink, setSmsConsent, textDriver, setDriverConsent, checkDriverRoadCall, setStopCommitment, setTaskDone, setTaskOwner } from '../../truckmateStore';
import CallButton, { TextButton, AiCallButton } from './CallButton';
import DriverThread from './DriverThread';
import BreakdownButton from './Breakdown';
import ReportBreakdown from './ReportBreakdown';
import DropZone from './DropZone';
import DriverAppBox, { useDriverApp, driverAppSummary, driverAppTone } from './DriverAppBox';
import DriverStops, { driverStopsSummary } from './DriverStops';
import ArtisStop from './ArtisManifest';
import { IcTruck, IcUser, IcPin, IcRoute, IcClock, IcAlert, IcAlertRound, IcDoc, IcList, IcMap, IcInfo, IcHeadset, IcFlag, IcExpand, IcChevron, IcBox, IcCalendar, IcWrench } from './cardIcons';
import {
  identity, locationModel, crewModel, healthModel, stopsModel, documentsModel, rateConModel,
  documentConflicts, viewLabel, NOT_REPORTED, ago, shortDate, friendlyAppt, apptParts, nextParts, hoursRows, phoneLabel, lateFlag, isDown, stopTimer, ocModel, packetModel, tripSheetModel, timesModel, contactFor, PAGE_TYPE_LABEL, OPTIN_SCRIPT, stopEta, stopLocalTime, isBrokerLoad,
} from './model';

// Leaflet only loads when a dispatcher opens an OC trip history.
const OcHistory = lazy(() => import('./OcHistory'));
const EmailBox = lazy(() => import('./EmailBox'));
const StatusMail = lazy(() => import('./EmailBox').then((m) => ({ default: m.StatusMail })));
const LoadTasks = lazy(() => import('./EmailBox').then((m) => ({ default: m.LoadTasks })));

// One truck / trip section of the dispatch workspace. Expansion state lives in
// the parent (isOpen/setOpen) so live refreshes never collapse what you opened.

function Disclosure({ id, isOpen, setOpen, className, summary, children }) {
  const open = isOpen(id);
  return (
    <details className={className} open={open} onToggle={(e) => { if (e.currentTarget.open !== open) setOpen(id, e.currentTarget.open); }}>
      <summary>{summary}</summary>
      {open ? children : null}
    </details>
  );
}

// In-page viewer for a stored original (PDF in a frame, images as images).
// Loading / denied / missing states are shown; the private URL is freed on close.
// The next trip TruckMate already has assigned to this truck (booked before this run ends).
const place = (z) => String(z || '').replace(/,\s*\d{5}.*$/, '').replace(/\b([A-Z])([A-Z]{2,})\b/g, (m, a, b) => a + b.toLowerCase());   // city words; 2-letter states stay
// "Is this driver broken down?" → Jarvis texts the driver the road-call questions + a photo link, calls if no answer
function CheckDriverButton({ trip }) {
  const [state, setState] = useState('');
  const go = async () => {
    const note = window.prompt(`Ask Jarvis to check on the driver of trip ${trip} (breakdown / tire)? Add what you know (optional):`, '');
    if (note === null) return;
    setState('…');
    try { const rc = await checkDriverRoadCall(trip, note || null); setState(`Road call ${rc.id} — Jarvis ${rc.check && rc.check.texted ? 'texted the driver' : 'is calling the driver'}`); } catch (e) { setState(e.message); }
  };
  return (<>
    <button type="button" className="tmw-btn tmw-btn-sm" onClick={go} title="Jarvis asks the driver what's wrong (tire / mechanical), which tire, the size, where — and builds the road call for Michelin"><IcHeadset /> Check driver</button>
    {state && <span className="tmw-small tmw-act-note">{state}</span>}
  </>);
}

// One labeled row of the card's status lists. Tone comes from the same rules the chips used.
// onOpen adds the chevron that opens its detail.
function StatusRow({ label, tone, onOpen, openLabel, expanded, children }) {
  return (
    <div className="tmw-sr" data-tone={tone || undefined}>
      <span className="tmw-sr-label">{label}</span>
      <div className="tmw-sr-value">{children}</div>
      {onOpen && <button type="button" className="tmw-sr-go" aria-label={openLabel || `Open ${label}`} aria-expanded={expanded} onClick={onOpen}><IcChevron /></button>}
    </div>
  );
}

const tmNextTone = (n) => { const tm = n.timing || null; return tm && tm.state === 'late' ? 'critical' : (n.tempChange || (tm && tm.state === 'tight')) ? 'warning' : undefined; };
const nextLoadTone = (n) => (n.verified ? undefined : 'warning');
const holdTone = (h) => (h.kind === 'pickup_delayed' || h.kind === 'breakdown' ? 'critical' : 'warning');

function TmNextChip({ n }) {
  const [open, setOpen] = useState(false);
  const temp = n.tempChange ? `Reefer ${n.tempChange.next.join('/')}°F next (now ${n.tempChange.now.join('/')}°F)` : null;
  const tm = n.timing || null;
  const hm = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${Math.round(m % 60)}m` : `${Math.round(m)}m`);
  const when = (ms) => (ms ? new Date(ms).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' }) : '—');
  const fit = tm && tm.state === 'late' ? `⚠ ${hm(tm.lateMin)} late for the ${tm.lateAt}` : tm && tm.state === 'tight' ? `⚠ tight — ${hm(tm.slackMin)} to spare` : tm && tm.state === 'ok' ? '✓ on time' : null;
  return (
    <StatusRow label="Next in TruckMate" tone={tmNextTone(n)}>
      <button type="button" className="tmw-sr-btn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {n.trip} · {place(n.from) || '—'} → {place(n.to) || '—'}{fit ? ` · ${fit}` : ''}{temp ? ` · ⚠ ${temp}` : ''}
      </button>
      {open && (
        <div className="tmw-small tmw-sr-more">
          <div><b>Trip {n.trip}</b> ({n.status || 'assigned'}) · {place(n.from)} → {place(n.to)}{n.createdBy ? ` · added by ${n.createdBy}` : ''}{n.createdAt ? ` ${new Date(n.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}</div>
          {(n.bills || []).map((b) => <div key={b.bill}>{b.bill}{b.billTo ? ` · ${b.billTo}` : ''}{b.stop ? ` · ${place(b.stop)}` : ''}{b.deliverBy ? ` · deliver by ${new Date(b.deliverBy).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}{b.temp != null ? ` · ${b.temp}°F` : ''}</div>)}
          {n.billCount > (n.bills || []).length && <div>+{n.billCount - n.bills.length} more bills</div>}
          {tm && tm.state !== 'unknown' && (
            <div style={{ marginTop: 4, color: tm.state === 'late' ? '#b91c1c' : tm.state === 'tight' ? '#b45309' : undefined }}>
              Empty {tm.emptyAt ? `at ${place(tm.emptyAt)} ` : ''}≈ {when(tm.emptyAtMs)} · {tm.deadheadMi} mi to {place(n.from)} → there ≈ {when(tm.pickupEtaMs)}{tm.pickupMs ? ` (pickup ${when(tm.pickupMs)})` : ''}
              {tm.deliverEtaMs ? ` · delivers ≈ ${when(tm.deliverEtaMs)} vs ${tm.deadlineKind} ${when(tm.deadlineMs)}` : ''} — {fit}
            </div>
          )}
          {tm && tm.state === 'unknown' && <div style={{ marginTop: 4, color: '#6b7280' }}>Can't time the next load yet ({tm.why}).</div>}
          {temp && <div style={{ color: '#b45309', marginTop: 4 }}>⚠ {temp} — set the reefer for the next load before pickup.</div>}
        </div>
      )}
    </StatusRow>
  );
}

// ARTIS (Florida Beauty's EDI): this load's customers' boxes scanned at the terminal vs expected. Amber
// when boxes are still missing before the truck leaves. Always on the card; read-only.
const artisTone = (a) => (a.alert || (a.sheetDiffs || []).length ? 'warning' : a.missing === 0 ? 'ok' : undefined);
function ArtisBoxes({ a, open, onToggle }) {
  return (
    <StatusRow label="ARTIS boxes" tone={artisTone(a)} onOpen={onToggle} openLabel="ARTIS by customer" expanded={open}>
      <b className="numeric">{a.scanned.toLocaleString()} / {a.boxes.toLocaleString()}</b> received · <span className="tmw-sr-strong">{a.missing ? `${a.missing} not received` : 'All received'}</span>
      {open && (
        <div className="tmw-small tmw-sr-more">
          <div><b>ARTIS · ship date {a.shipDate}</b></div>
          {(a.stops || []).map((c) => (
            <div key={c.consigneeId || c.consignee} style={c.missing ? { color: '#b45309' } : undefined}>{c.consignee}{c.consigneeId ? ` (${c.consigneeId})` : ''}: {c.scanned}/{c.boxes} received{c.missing ? ` — ${c.missing} not received${(c.shippers || []).length ? ` (${c.shippers.map((x) => `${x.shipper} ${x.missing}`).join(', ')})` : ''}` : ''}</div>
          ))}
          {(a.sheetDiffs || []).map((d) => <div key={`d${d.consignee}`} style={{ color: '#b45309' }}>Trip sheet says {d.sheet} boxes for {d.consignee} — ARTIS has {d.artis} (ARTIS is correct)</div>)}
          {a.missing > 0 && <div style={{ color: '#b45309', marginTop: 4 }}>⚠ Never received at our terminal — verify whether they were rejected, or contact the shipper.</div>}
        </div>
      )}
    </StatusRow>
  );
}

// The truck's next load (a rate con dispatch sent for after this run). Verified once TruckMate has it.
function NextLoadChip({ n }) {
  const [open, setOpen] = useState(false);
  const [doc, setDoc] = useState(false);
  const rc = n.rc || {};
  const at = (s) => (s ? [s.city, s.date, s.time].filter(Boolean).join(' · ') : '—');
  return (
    <StatusRow label="Next load" tone={nextLoadTone(n)}>
      <button type="button" className="tmw-sr-btn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {rc.broker || 'rate con'}{rc.loadNumber ? ` ${rc.loadNumber}` : ''} · {(rc.pickup && rc.pickup.city) || '—'} → {(rc.delivery && rc.delivery.city) || '—'} · {n.verified ? `✓ ${n.verified.by}${n.verified.trip ? ` ${n.verified.trip}` : ''}` : 'not in TruckMate yet'}
      </button>
      {open && (
        <div className="tmw-small tmw-sr-more">
          <div><b>Truck {n.truck}</b> next load ({n.truckHow || 'from the rate con'}) · sent by {n.from || 'dispatch'} {n.at ? `· ${new Date(n.at).toLocaleString()}` : ''}</div>
          <div>Pickup: {at(rc.pickup)} · Delivery: {at(rc.delivery)}{rc.stops > 2 ? ` · ${rc.stops} stops` : ''}</div>
          {(rc.bill || rc.rateText || rc.rate) && <div>{rc.bill ? `FBF bill ${rc.bill}` : ''}{rc.bill && (rc.rateText || rc.rate) ? ' · ' : ''}{rc.rateText || (rc.rate != null ? `$${rc.rate}` : '')}{rc.tempSetting ? ` · temp ${rc.tempSetting}` : ''}</div>}
          {(rc.notes || []).length > 0 && <div>Notes: {rc.notes.join(' · ')}</div>}
          <div>{n.verified ? `Confirmed by ${n.verified.by}${n.verified.trip ? ` (trip ${n.verified.trip})` : ''}.` : 'Not in TruckMate yet — Jarvis marks it confirmed when the trip is booked, and drops it when that trip starts.'}</div>
          {(rc.docIds || []).length > 0 && <button type="button" className="tmw-btn tmw-btn-sm" style={{ marginTop: 6 }} onClick={() => setDoc(true)}>View rate con</button>}
        </div>
      )}
      {doc && <DocViewer file={{ ids: rc.docIds, filename: `Rate con ${rc.loadNumber || ''}`.trim(), mediaType: 'application/pdf' }} onClose={() => setDoc(false)} />}
    </StatusRow>
  );
}

export function DocViewer({ file, onClose }) {
  const [state, setState] = useState({ loading: true });
  const [reveal, setReveal] = useState(false);
  const idsKey = (file.ids || []).join(',');   // merged trip sheet (stable across re-renders)
  const mergedName = file.filename;
  // several pages (a 2-page trip sheet): each page on its own too — phones (iPhone Safari / the app) only show the
  // FIRST page of a PDF in a frame, so one merged PDF would hide page 2
  const [pages, setPages] = useState([]);
  useEffect(() => {
    let url = null; let alive = true; const pageUrls = [];
    setState({ loading: true }); setPages([]);
    (file.rundownTrip ? fetchRundownPdf(file.rundownTrip) : idsKey ? fetchMergedDocs(idsKey.split(','), { name: mergedName }) : fetchStoredDoc(file.id, { reveal }))
      .then((d) => { url = d.url; if (alive) setState({ url: d.url, type: d.type }); else URL.revokeObjectURL(d.url); })
      .catch((e) => { if (alive) setState({ error: e.message || 'Could not open the document.', restricted: !!e.restricted }); });
    if (idsKey && idsKey.includes(',')) {
      Promise.all(idsKey.split(',').map((id) => fetchStoredDoc(id).then((d) => { pageUrls.push(d.url); return { id, ...d }; }).catch(() => null)))
        .then((list) => { if (alive) setPages(list.filter(Boolean)); });
    }
    return () => { alive = false; if (url) URL.revokeObjectURL(url); pageUrls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [file.id, file.rundownTrip, idsKey, mergedName, reveal]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const isPdf = /pdf/i.test(state.type || file.mediaType || '');
  // rendered at <body> level (inside the workspace theme) so clicks in the
  // viewer never toggle the section heading it was opened from
  return createPortal(
    <div className="truckmate-workspace tmw-viewer-root"><div className="tmw-viewer" role="dialog" aria-modal="true" aria-label={`${file.filename || 'Document'} viewer`} onClick={onClose}>
      <div className="tmw-viewer-box" onClick={(e) => e.stopPropagation()}>
        <div className="tmw-viewer-head">
          <span className="tmw-small"><b>{file.filename || 'Uploaded document'}</b> · {file.mediaType}{file.version ? ` · version ${file.version}` : ''}{file.page ? ` · page ${file.page}` : ''}{file.uploadedBy ? ` · ${file.uploadedBy}` : ''}</span>
          <span className="tmw-file-list">
            {state.url && <a className="tmw-btn tmw-btn-sm" href={state.url} target="_blank" rel="noreferrer">Open in new tab ↗</a>}
            <button type="button" className="tmw-btn tmw-btn-sm" onClick={onClose} autoFocus>Close</button>
          </span>
        </div>
        <div className="tmw-viewer-body">
          {state.loading && <div className="tmw-note">Loading the original…</div>}
          {state.error && (
            <div className="tmw-conflict" role="alert">
              {state.error}
              {state.restricted && !reveal && (
                <div style={{ marginTop: 8 }}>
                  <div className="tmw-small">Only open it if you need it for this load. Your name and the time are recorded.</div>
                  <button type="button" className="tmw-btn tmw-btn-sm tmw-btn-primary" style={{ marginTop: 6 }} onClick={() => setReveal(true)}>View restricted page</button>
                </div>
              )}
            </div>
          )}
          {pages.length > 1 ? pages.map((pg, i) => (
            <div key={pg.id} className="tmw-viewer-page">
              <div className="tmw-small" style={{ margin: '6px 0 2px' }}><b>Page {i + 1} of {pages.length}</b></div>
              {/pdf/i.test(pg.type || '') ? <iframe title={`${file.filename || 'Document'} — page ${i + 1}`} src={pg.url} /> : <img alt={`${file.filename || 'Document'} — page ${i + 1}`} src={pg.url} />}
            </div>
          )) : state.url && (isPdf
            ? <iframe title={file.filename || 'Document'} src={state.url} />
            : <img alt={file.filename || 'Uploaded document'} src={state.url} />)}
        </div>
      </div>
    </div></div>,
    document.body,
  );
}

// The load's full rundown PDF (everything that happened + the original paperwork).
export function RundownButton({ trip, label = 'Rundown PDF' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="tmw-btn tmw-btn-sm" title="Everything on this load so far: timeline, stops, alerts, calls and texts, paperwork"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}><IcList /> {label}</button>
      {open && <DocViewer file={{ rundownTrip: String(trip), filename: `Rundown ${trip}`, mediaType: 'application/pdf' }} onClose={() => setOpen(false)} />}
    </>
  );
}

// Opens the trip's manifest (1–2 pages) as one document.
function TripSheetButton({ trip, primary = false, label }) {
  const ts = tripSheetModel(trip);
  const [open, setOpen] = useState(false);
  if (!ts) return null;
  return (
    <>
      <button type="button" className={`tmw-btn tmw-btn-sm${primary ? ' tmw-btn-primary' : ''}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}>
        <IcDoc /> {label || 'Trip sheet'}{ts.pages > 1 ? ` · ${ts.pages} pages` : ''}
      </button>
      {open && <DocViewer file={{ ids: ts.ids, filename: ts.name, mediaType: 'application/pdf', uploadedBy: ts.uploadedBy }} onClose={() => setOpen(false)} />}
    </>
  );
}

function DocButtons({ group }) {
  const [viewing, setViewing] = useState(null);
  if (!group || !group.files.length) return null;
  const many = group.files.length > 1;
  return (
    <span className="tmw-file-list">
      {group.files.map((f, i) => (
        <button key={f.id} type="button" className="tmw-btn tmw-btn-sm"
          title={`${f.filename || 'Uploaded file'} · ${f.mediaType}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setViewing(f); }}>
          {viewLabel(f)}{many ? ` · page ${f.page || i + 1}` : ''} ↗
        </button>
      ))}
      {viewing && <DocViewer file={viewing} onClose={() => setViewing(null)} />}
    </span>
  );
}

// ---- accordion row: pale gray heading row, white contained body -------------
function Section({ id, label, summary, tone, isOpen, setOpen, children }) {
  const open = isOpen(id);
  return (
    <details className="tmw-acc" data-tone={tone || undefined} open={open}
      onToggle={(e) => { if (e.currentTarget.open !== open) setOpen(id, e.currentTarget.open); }}>
      <summary className="tmw-acc-row">
        <span className="tmw-acc-label">{label}</span>
        <span className="tmw-acc-sum">{summary}</span>
        <span className="tmw-chev" aria-hidden="true">›</span>
      </summary>
      {open ? <div className="tmw-acc-body">{children}</div> : null}
    </details>
  );
}

// Counts up on the card while the truck sits; the watch board says when it stopped.
function StopClock({ stopped, team }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { if (!stopped) return undefined; const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, [stopped]);
  const s = stopTimer(stopped, team, Math.max(now, Date.now()));
  return s ? <span className="tmw-late-flag" data-level={s.level}><IcClock size={14} />{s.label}</span> : null;
}

// The waiting time clock at a shipper or receiver, once it's due. Turned down: nothing shows.
function WaitClock({ w }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);
  const mins = Math.floor((Math.max(now, Date.now()) - w.startMs) / 60000);
  if (w.status === 'declined' || mins < 0) return null;
  return <span className="tmw-late-flag" data-level={w.status === 'approved' ? 'risk' : 'calm'}><IcClock size={14} />Waiting {mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`}{w.status === 'approved' ? '' : ' · not approved'}</span>;
}

// To-dos right where they're noticed: what it says, and Done or I'm on it, without opening anything.
const TODOS_SHOWN = 3;
function CardTodos({ trip, tasks, onChanged, onMore }) {
  const [gone, setGone] = useState([]);
  const [mine, setMine] = useState({});
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const left = tasks.filter((t) => !gone.includes(t.id));
  const act = async (t, fn, after) => { setBusy(t.id); setErr(''); try { await fn(); after(); if (onChanged) onChanged(); } catch (e) { setErr(e.message || 'That did not save.'); } finally { setBusy(''); } };
  return (
    <div className="tmw-todos">
      {left.slice(0, TODOS_SHOWN).map((t) => {
        const owner = (t.owner && t.owner.by) || mine[t.id];
        return (
          <div key={t.id} className="tmw-todo">
            <span>{t.title}{t.urgency === 'urgent' ? <b className="tmw-task-urgent"> urgent</b> : null}{owner ? <span className="tmw-small"> · {owner === 'You' ? "You're on it" : `${owner} is on it`}</span> : null}</span>
            <span className="tmw-todo-acts">
              {!owner && <button type="button" className="tmw-btn tmw-btn-sm" disabled={busy === t.id} onClick={() => act(t, () => setTaskOwner(trip, t.id), () => setMine((m) => ({ ...m, [t.id]: 'You' })))}>I'm on it</button>}
              <button type="button" className="tmw-btn tmw-btn-sm" disabled={busy === t.id} onClick={() => act(t, () => setTaskDone(trip, t.id, true), () => setGone((g) => [...g, t.id]))}>Done</button>
            </span>
          </div>
        );
      })}
      {left.length > TODOS_SHOWN && <button type="button" className="tmw-link-btn" onClick={onMore}>{left.length - TODOS_SHOWN} more</button>}
      {err && <div className="tmw-conflict" role="alert">{err}</div>}
    </div>
  );
}

// ---- sticky navy identity header ---------------------------------------------
function IdentityHeader({ trip, selected, isOpen, setOpen, oc, late, stopped, team }) {
  const id = identity(trip);
  const k = `${trip.tripNumber}:bills`;
  return (
    <>
      <header className="tmw-identity" data-oc={oc ? 'true' : undefined}>
        <span className="tmw-id-ico"><IcTruck size={30} /></span>
        <div className="tmw-id-main">
          <div className="tmw-id-top">
            {oc
              ? <span className="tmw-id-truck">OC <strong>{oc.carrierName}</strong></span>
              : <span className="tmw-id-truck">{trip.powerUnit ? 'Truck' : 'Trip'} <strong className="numeric">{trip.powerUnit ? id.truck : id.trip}</strong></span>}
            {!oc && !trip.powerUnit && <span className="tmw-selected-badge">Truck unassigned</span>}
            {oc && <span className="tmw-oc-badge">Outside carrier</span>}
            {isDown(trip) && <span className="tmw-late-flag tmw-down-flag" data-level="down"><IcWrench /> BROKEN DOWN</span>}
            {late && <span className="tmw-late-flag" data-level={late.level}><IcClock size={14} />{late.label}</span>}
            {!oc && <StopClock stopped={stopped} team={team} />}
            {trip.waiting && <WaitClock w={trip.waiting} />}
            {selected && <span className="tmw-selected-badge">Selected</span>}
          </div>
          <div className="tmw-id-line">
            {oc && <span className="tmw-id">Truck <strong className="numeric">{oc.code || oc.truck || '—'}</strong></span>}
            <span className="tmw-id">Trailer <strong className="numeric">{(oc && oc.trailer) || id.trailer || '—'}</strong></span>
            {(oc || trip.powerUnit) && <span className="tmw-id">Trip <strong className="numeric">{id.trip}</strong></span>}
            {trip.rateCon && trip.rateCon.loadNumber && <span className="tmw-id" title={trip.rateCon.broker || 'Broker'}>Broker load # <strong className="numeric">{trip.rateCon.loadNumber}</strong></span>}
          </div>
          <div className="tmw-id-line">
            <span className="tmw-id">Bill <strong className="numeric">{id.leadBill || 'Not provided'}</strong>
              {id.moreBills > 0 && (
                <button type="button" className="tmw-bill-toggle" aria-expanded={isOpen(k)} onClick={() => setOpen(k, !isOpen(k))}>
                  +{id.moreBills} bill{id.moreBills === 1 ? '' : 's'}
                </button>
              )}
            </span>
          </div>
        </div>
      </header>
      {id.moreBills > 0 && isOpen(k) && (
        <div className="tmw-bill-list" aria-label={`All bills for trip ${id.trip}`}>
          {id.bills.map((b) => <span key={b} className="numeric">{b}</span>)}
          <button type="button" className="tmw-btn tmw-btn-sm" onClick={() => { try { navigator.clipboard.writeText(id.bills.join('\n')); } catch { /* ignore */ } }}>Copy all</button>
        </div>
      )}
    </>
  );
}

const healthShort = (h) => (h.faults.length ? `${h.faults.length} engine fault${h.faults.length === 1 ? '' : 's'}` : (h.status.key === 'ok' ? 'No faults reported' : 'No engine data'));

// ---- compact overview: everything a dispatcher needs without expanding -------
// ⏸ From a dispatcher's email: pickup delayed / driver or truck changed. Clears itself once the truck is at the shipper.
const HOLD_LABEL = { pickup_delayed: 'Pickup on hold', driver_changed: 'Driver changed', truck_changed: 'Truck changed', breakdown: 'Breakdown reported', delay: 'Delay reported' };
function HoldChip({ trip, onChanged }) {
  const h = trip.hold;
  const [busy, setBusy] = useState(false);
  const clear = async () => {
    if (!window.confirm(`Clear "${HOLD_LABEL[h.kind] || 'Update'}" on trip ${trip.tripNumber}?`)) return;
    setBusy(true);
    try { await clearHold(trip.tripNumber); if (onChanged) onChanged(); } catch (e) { window.alert(e.message || 'Could not clear.'); } finally { setBusy(false); }
  };
  return (
    <StatusRow label="Hold" tone={holdTone(h)}>
      <span title={`From ${h.from || 'email'}${h.subject ? ` — ${h.subject}` : ''}`}>{HOLD_LABEL[h.kind] || 'Update'}: {h.note}{h.newPickupAt ? ` · new pickup ${h.newPickupAt.replace('T', ' ')}` : ''}</span>
      <button type="button" className="tmw-btn tmw-btn-sm tmw-sr-act" onClick={clear} disabled={busy}>Clear</button>
    </StatusRow>
  );
}

// ✓ Mark finished: the load leaves Active trips for Delivered (with who + why), its rundown is made.
function FinishButton({ trip, hint, onChanged }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    const note = window.prompt(`Mark trip ${trip.tripNumber} finished? It moves to Delivered and its rundown is made.\n\nReason (optional):`, hint ? hint.replace(/^Probably finished — /, '') : '');
    if (note === null) return;
    setBusy(true);
    try { await finishTrip(trip.tripNumber, note); if (onChanged) onChanged(); } catch (e) { window.alert(e.message || 'Could not mark finished.'); } finally { setBusy(false); }
  };
  return <button type="button" className="tmw-btn tmw-btn-sm" onClick={go} disabled={busy}><IcFlag /> {busy ? 'Finishing…' : 'Mark finished'}</button>;
}

// The texting-consent state of a driver's number, as it reads on the card and in the Details panel. Pure.
export function consentLabel(d) {
  if (d.phoneSource === 'Trip sheet') return 'Number from the trip sheet — texts need a Samsara profile';
  if (d.optedOut) return `Replied STOP on ${shortDate(Date.parse(d.optedOut))} — no texts`;
  return d.consent ? `Agreed · ${d.consent.by}` : 'No consent recorded yet';
}

// Why Text is locked for this driver, if it is. Pure.
export const textBlocked = (d) => (d.optedOut ? 'This driver replied STOP' : d.consent ? null : 'Record the driver’s consent first (Driver crew)');

// A company driver's phone on the card: console users only, with Call and Text. Text stays locked until
// consent is on record; the server checks it again before anything goes out.
function DriverPhone({ d, trip }) {
  if (!d.phone) return <div className="tmw-cd-phone tmw-small">Phone not on file</div>;
  const name = d.name || 'driver';
  return (
    <div className="tmw-cd-phone">
      <span className="tmw-cd-num"><a href={`tel:${d.phone}`}>{phoneLabel(d.phone)}</a>{d.phoneSource === 'Trip sheet' && <span className="tmw-from"> · from trip sheet</span>}</span>
      <CallButton phone={d.phone} trip={trip} label={name} />
      {d.phoneSource === 'Samsara' && <TextButton trip={trip} which={d.which} name={name} phone={d.phone} blocked={textBlocked(d)} />}
      <span className="tmw-cd-consent" data-ok={d.consent ? 'true' : undefined}>Texts: {consentLabel(d)}</span>
    </div>
  );
}

const BlockTitle = ({ icon, children }) => <div className="tmw-cc-h">{icon}{children}</div>;

function CrewBlock({ crew, trip }) {
  return (
    <section className="tmw-cc" aria-label="Crew">
      <div className="tmw-cc-head"><span className="tmw-cc-h">Driver crew</span><span className="tmw-chip">{crew.mode === 'Unverified' ? 'Solo / Team unverified' : crew.mode}</span></div>
      {crew.drivers.length === 0 && <div className="tmw-small">No driver assigned</div>}
      {crew.drivers.map((d) => (
        <div className="tmw-cd" key={d.role}>
          <span className="tmw-cd-ava"><IcUser size={22} /></span>
          <div className="tmw-cd-who">
            <div className="tmw-cd-name"><b>{d.name}</b></div>
            {crew.atWheel.verified && crew.atWheel.name === d.name ? <div className="tmw-wheel-tag">At the wheel</div> : null}
            <div className="tmw-small">{d.status}{d.stale && d.statusKey ? ' (stale)' : ''}</div>
          </div>
          <dl className="tmw-cd-hours">
            <div><dt>Drive left</dt><dd className="numeric">{d.driveLeft}</dd></div>
            <div><dt>Shift left</dt><dd className="numeric">{d.shiftLeft}</dd></div>
            {d.cycleLeft && <div><dt>Cycle left</dt><dd className="numeric">{d.cycleLeft}</dd></div>}
          </dl>
          <DriverPhone d={d} trip={trip.tripNumber} />
        </div>
      ))}
    </section>
  );
}

function OcBlock({ oc, trip }) {
  return (
    <section className="tmw-cc" aria-label="Outside carrier">
      <div className="tmw-cc-head"><span className="tmw-cc-h">Outside carrier</span><span className="tmw-chip">{oc.method}</span></div>
      <div className="tmw-ov-driver"><b>{oc.carrierName}</b>{oc.truck ? <span className="tmw-small"> · TK # {oc.truck}</span> : null}{oc.trailer ? <span className="tmw-small"> · Trailer # {oc.trailer}</span> : null}</div>
      <div className="tmw-small">Carrier phone: <b>{oc.dispatchPhone || 'not on file'}</b></div>
      <div className="tmw-small tmw-ov-person"><Person name={oc.driverName} phone={oc.driverPhone} trip={trip.tripNumber} /></div>
      {oc.crew === 'team' && <div className="tmw-small tmw-ov-person"><Person name={oc.driver2Name} phone={oc.driver2Phone} trip={trip.tripNumber} which={2} /></div>}
    </section>
  );
}

function LocationBlock({ trip, oc, loc }) {
  if (oc) {
    return (
      <section className="tmw-cc" aria-label="Last check-in">
        <BlockTitle icon={<IcPin />}>Last check-in</BlockTitle>
        <div className="tmw-ov-value">{oc.last ? (oc.last.location || oc.last.text) : 'No check-in yet'}</div>
        <div className="tmw-small">{oc.lastLabel}{oc.overdue ? ' · update overdue' : ''}</div>
        <div className="tmw-small">{loc.gps ? <>Current location: <b>{loc.gps}</b> · {loc.gpsAge || 'time unknown'}{loc.speedMph != null ? ` · ${Math.round(loc.speedMph)} mph` : ''}</> : 'No GPS from our fleet — outside carrier'}</div>
      </section>
    );
  }
  const stale = loc.gps && loc.gpsStale;
  return (
    <section className="tmw-cc" aria-label="Location">
      <BlockTitle icon={<IcPin />}>{stale ? 'Last known location' : 'Current location'}</BlockTitle>
      <div className="tmw-ov-value">{loc.gps || 'GPS unavailable'}</div>
      <div className="tmw-small" data-stale={stale ? 'true' : undefined}>{loc.speedMph != null ? `${Math.round(loc.speedMph)} mph · ` : ''}{loc.gps ? `${loc.source || 'GPS'} updated ${loc.gpsAge || 'time unknown'}${loc.gpsStale ? ' · stale' : ''}` : 'No live position'}</div>
      {loc.truckmateZone && <div className="tmw-loc-zone"><span>TruckMate zone:</span> {loc.truckmateZone}</div>}
      {(!loc.gps || loc.gpsStale) && trip.artisLoc && trip.artisLoc.truck && trip.artisLoc.truck.location && (
        <div className="tmw-small">ARTIS: <b>{trip.artisLoc.truck.location}</b> · {ago(new Date(trip.artisLoc.truck.at).toISOString()) || 'time unknown'}{trip.artisLoc.truck.source ? ` (${trip.artisLoc.truck.source})` : ''}</div>
      )}
    </section>
  );
}

const etaLabel = (ms) => { const d = new Date(ms); return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`; };

function RouteBlock({ trip, glance, late, onOpenStops }) {
  const next = nextParts(glance.nextLabel);
  const appt = glance.appointment ? apptParts(glance.appointment) : null;
  const other = appt && appt.stop && appt.stop !== next.stop;   // the nearest appointment can be a later stop than the next one
  const miles = glance.milesTo != null ? `${Number(glance.milesTo).toLocaleString()} mi ${glance.milesToLabel === 'to go' ? 'remaining' : glance.milesToLabel}` : null;
  const qb = glance.queuedBehind;
  const delay = late && String(late.title).match(/^(.*?) by (~.+)$/);
  return (
    <section className="tmw-cc tmw-cc-route" aria-label="Route and next stop">
      <BlockTitle icon={<IcRoute />}>Route</BlockTitle>
      <div className="tmw-route-line">{trip.origin || '—'} → {trip.destination || '—'}</div>
      <button type="button" className="tmw-next" aria-label={`Open outbound stops. Next stop: ${glance.nextLabel || 'not reported'}`} onClick={onOpenStops}>
        {next.stop && <span className="tmw-stop-badge">{next.stop}</span>}
        <span className="tmw-next-body">
          <span className="tmw-next-label">{next.stop ? `Next · Stop ${next.stop}` : 'Next'}</span>
          <span className="tmw-next-name">{next.name || '—'}</span>
          {(next.city || miles) && <span className="tmw-next-city">{[next.city, miles].filter(Boolean).join(' · ')}</span>}
        </span>
        <IcChevron />
      </button>
      <div className="tmw-next-times">
        <div>
          <IcClock size={18} />
          <span><span className="tmw-next-label">Appointment</span><b>{appt ? appt.when : NOT_REPORTED}</b>
            {appt && <span className="tmw-small">{[other ? `Stop ${appt.stop}` : null, appt.handwritten ? 'Handwritten' : null, 'Timezone unstated'].filter(Boolean).join(' · ')}</span>}</span>
        </div>
        <div>
          <IcClock size={18} />
          <span><span className="tmw-next-label">Estimated arrival</span><b>{glance.hubHold ? `Still at the ${glance.hubHold} hub — not departed yet` : glance.eta ? etaLabel(glance.eta) : (glance.atStop ? 'At stop' : qb ? `after trip ${qb.trip}` : NOT_REPORTED)}</b>
            {glance.eta && !glance.hubHold && <span className="tmw-small">Estimate{qb ? ' — after the current trip' : ''}</span>}</span>
        </div>
      </div>
      {late && (
        <div className="tmw-next-late" data-tone={late.severity === 'critical' ? 'critical' : 'warning'}>
          <IcAlertRound size={20} />
          <span>
            <b>{delay ? `Estimated delay: ${delay[2]}` : 'Late warning'}</b>
            <span>{delay ? delay[1] : late.title}</span>
            {appt && <span>{appt.handwritten ? 'Verify handwritten appointment and timezone' : 'Verify appointment timezone'}</span>}
          </span>
        </div>
      )}
      {qb && <div className="tmw-small" style={{ color: '#92400e', marginTop: 6 }}>⏭ Next load for truck {qb.truck || ''} — it's still on trip {qb.trip}. This load shows inside that trip until it's dispatched.</div>}
    </section>
  );
}

function Overview({ trip, meta, glance, incidents, health, conflicts, selected, onSelect, allOpen, onToggleAll, mapOpen, onToggleMap, oc, onChanged, onOpenSection }) {
  const [artisOpen, setArtisOpen] = useState(false);
  const loc = locationModel(trip);
  const crew = glance.crew;
  // the late warning sits with the ETA; every other open issue stays in the status lists
  const late = incidents.groups.find((g) => g.code === 'late-risk') || null;
  const others = incidents.groups.filter((g) => g !== late);
  const top = others[0];
  const moreOpen = incidents.count - (late ? late.items.length : 0) - 1;
  const tm = timesModel(trip);
  const a = trip.artis;
  const select = (e) => { if (e.target.closest('button, a, input, label')) return; onSelect(); };
  const toggleArtis = () => setArtisOpen((v) => !v);
  const row = (tone, key, el) => ({ tone, key, el });
  const tasksOpen = (trip.tasks || []).filter((t) => !t.done);
  const pf = trip.pickupFollow;
  const rows = [
    trip.breakdown && trip.breakdown.on && row('critical', 'bd', <StatusRow label="Breakdown" tone="critical">Since {shortDate(Date.parse(trip.breakdown.at))} · {trip.breakdown.by}</StatusRow>),
    trip.hold && row(holdTone(trip.hold), 'hold', <HoldChip trip={trip} onChanged={onChanged} />),
    top && row(top.severity === 'critical' ? 'critical' : 'warning', 'issue', <StatusRow label="Open issue" tone={top.severity === 'critical' ? 'critical' : 'warning'}>{top.title}{moreOpen > 0 ? ` (+${moreOpen} more)` : ''}</StatusRow>),
    a ? row(artisTone(a), 'artis', <ArtisBoxes a={a} open={artisOpen} onToggle={toggleArtis} />) : row(undefined, 'artis', <StatusRow label="ARTIS boxes">Not available for this trip</StatusRow>),
    a && (a.sheetDiffs || []).length > 0 && row('warning', 'src', <StatusRow label="Sources" tone="warning" onOpen={toggleArtis} openLabel="ARTIS differences by customer" expanded={artisOpen}>Trip sheet differs from ARTIS</StatusRow>),
    ...(oc ? [
      row(oc.overdue ? 'warning' : undefined, 'ocu', <StatusRow label="Carrier updates" tone={oc.overdue ? 'warning' : undefined}>{oc.overdue ? `Carrier update overdue · ${oc.lastLabel}` : `Carrier updated · ${oc.lastAgo}`}</StatusRow>),
      oc.missing.length > 0 && row('warning', 'ocm', <StatusRow label="Driver info" tone="warning">{oc.crew === 'team' ? 'Team' : 'Solo'} driver info missing ({oc.missing.length})</StatusRow>),
    ] : [row(health.faults.length ? 'warning' : undefined, 'health', <StatusRow label="Truck health" tone={health.faults.length ? 'warning' : undefined} onOpen={() => onOpenSection('health')}>{healthShort(health)}</StatusRow>)]),
    trip.artisDoorTemp && row('warning', 'door', <StatusRow label="Door temperature" tone="warning">Loaded at {trip.artisDoorTemp.actual}°F · set {trip.artisDoorTemp.set}°F <span className="tmw-small">· ARTIS door check{trip.artisDoorTemp.atText ? ` ${trip.artisDoorTemp.atText}` : ''}{trip.artisDoorTemp.door ? ` · door ${trip.artisDoorTemp.door}` : ''}</span></StatusRow>),
    ...(trip.nextLoad || []).map((n) => row(nextLoadTone(n), `nl${n.id}`, <NextLoadChip n={n} />)),
    ...(trip.tmNext || []).map((n) => row(tmNextTone(n), `tm${n.trip}`, <TmNextChip n={n} />)),
    ...(trip.notes || []).filter((n) => n.kind === 'transfer').slice(-1).map((n) => row('warning', `t${n.id}`, <StatusRow label="Transfer" tone="warning"><span title={`${n.text} — ${n.by}`}>{[n.transfer && n.transfer.fromTruck && `truck ${n.transfer.fromTruck}`, n.transfer && n.transfer.toTruck && `→ truck ${n.transfer.toTruck}`].filter(Boolean).join(' ') || n.text}{n.transfer && n.transfer.place ? ` · ${n.transfer.place}` : ''}{n.transfer && n.transfer.at ? ` · ${n.transfer.at}` : ''}</span></StatusRow>)),
    trip.ocChat && trip.ocChat.unread > 0 && row('warning', 'chat', <StatusRow label="Driver messages" tone="warning">{trip.ocChat.unread} new message{trip.ocChat.unread === 1 ? '' : 's'} from the driver</StatusRow>),
    pf && pf.outcome && pf.outcome.source !== 'TruckMate: departed the shipper' && row(pf.outcome.status === 'rolling' ? undefined : 'warning', 'pfo', <StatusRow label="Pickup follow-up" tone={pf.outcome.status === 'rolling' ? undefined : 'warning'}>Driver: {({ rolling: 'rolling to pickup', delayed: 'pickup late', issue: 'has a problem' }[pf.outcome.status] || pf.outcome.status)}{pf.outcome.eta ? ` · ${pf.outcome.eta}` : ''}{pf.emailed ? ' · dispatch emailed' : ''}</StatusRow>),
    trip.finishHint && row('warning', 'fin', <StatusRow label="Finish check" tone="warning">{trip.finishHint}</StatusRow>),
    tasksOpen.length > 0 && row(tasksOpen.some((t) => t.urgency === 'urgent') ? 'critical' : 'warning', 'todo', <StatusRow label="Email to-dos" tone={tasksOpen.some((t) => t.urgency === 'urgent') ? 'critical' : 'warning'}><CardTodos trip={trip.tripNumber} tasks={tasksOpen} onChanged={onChanged} onMore={() => onOpenSection && onOpenSection('emails')} /></StatusRow>),
    conflicts > 0 && row('warning', 'conf', <StatusRow label="Source conflicts" tone="warning">{conflicts} source conflict{conflicts === 1 ? '' : 's'}</StatusRow>),
    ...(trip.notes || []).filter((n) => n.kind !== 'transfer').slice(-1).map((n) => row(undefined, `n${n.id}`, <StatusRow label="Note"><span title={`${n.by} · ${new Date(n.at).toLocaleString()}`}>{n.text.length > 70 ? `${n.text.slice(0, 70)}…` : n.text}</span></StatusRow>)),
    pf && !pf.outcome && pf.steps.length > 0 && row(undefined, 'pfs', <StatusRow label="Pickup follow-up">Jarvis following the pickup ({pf.steps.map((x) => ({ ack: 'got it', t60: '1 h check', t30: '30 min check' }[x] || x)).join(', ')} sent)</StatusRow>),
  ].filter(Boolean);
  const attention = [...rows.filter((r) => r.tone === 'critical'), ...rows.filter((r) => r.tone === 'warning')];
  const calm = [
    row(undefined, 'tm', <StatusRow label="TruckMate status">{meta.label}{incidents.count || attention.length ? '' : ' · no open issues'}</StatusRow>),
    ...rows.filter((r) => r.tone !== 'critical' && r.tone !== 'warning'),
  ];
  return (
    <div className="tmw-overview-card" onClick={select}>
      <div className="tmw-ov-grid">
        <div className="tmw-ov-col tmw-ov-crew">
          {oc ? <OcBlock oc={oc} trip={trip} /> : <CrewBlock crew={crew} trip={trip} />}
        </div>
        <div className="tmw-ov-col tmw-ov-location">
          <LocationBlock trip={trip} oc={oc} loc={loc} />
        </div>
        <div className="tmw-ov-col tmw-ov-route">
          <RouteBlock trip={trip} glance={glance} late={late} onOpenStops={() => onOpenSection('stops')} />
        </div>
        <div className="tmw-ov-col tmw-ov-operational">
          {attention.length > 0 && (
            <section className="tmw-cc tmw-ov-status" aria-label="Needs attention">
              <BlockTitle icon={<IcAlert />}>Needs attention</BlockTitle>
              <div className="tmw-sr-list">{attention.map((r) => <React.Fragment key={r.key}>{r.el}</React.Fragment>)}</div>
            </section>
          )}
          <section className="tmw-cc tmw-ov-status" aria-label="Status">
            <BlockTitle icon={<IcInfo />}>Status</BlockTitle>
            <div className="tmw-sr-list">{calm.map((r) => <React.Fragment key={r.key}>{r.el}</React.Fragment>)}</div>
          </section>
        </div>
        <div className="tmw-ov-col tmw-ov-assignment">
          <section className="tmw-cc tmw-ov-status" aria-label="Assignment and activity">
            <BlockTitle icon={<IcDoc />}>Assignment &amp; activity</BlockTitle>
            <dl className="tmw-ov-times">
              {trip.plan && <div><dt>Plan</dt><dd title={[trip.plan.next && trip.plan.next.info, trip.plan.notes].filter(Boolean).join(' · ')}>{[trip.plan.region, trip.plan.kind].filter(Boolean).join(' · ') || 'on Gus\'s sheet'}{trip.plan.next && trip.plan.next.bill ? ` · next ${trip.plan.next.bill}` : ''}</dd></div>}
              <div><dt>Owner</dt><dd>{incidents.owner || (incidents.count ? 'Unassigned' : '—')}</dd></div>
              <div><dt>Handling</dt><dd>{glance.handling.label === 'Rate con missing' ? 'Waiting on the rate con' : glance.handling.label}</dd></div>
              {tm && <div><dt>Created</dt><dd>{tm.created || 'not reported'}</dd></div>}
              {tm && <div><dt>Added</dt><dd>{tm.added || '—'}</dd></div>}
              {tm && <div><dt>Dispatched</dt><dd>{tm.dispatched}</dd></div>}
            </dl>
          </section>
        </div>
      </div>
      <div className="tmw-ov-actions" role="group" aria-label="Actions">
        <TripSheetButton trip={trip} />
        <RundownButton trip={trip.tripNumber} label="Rundown" />
        <button type="button" className="tmw-btn tmw-btn-sm" aria-expanded={mapOpen} onClick={onToggleMap} disabled={!trip.powerUnit}><IcMap /> {mapOpen ? 'Hide map' : 'Map'}</button>
        <button type="button" className={`tmw-btn tmw-btn-sm${selected ? '' : ' tmw-btn-primary'}`} aria-pressed={selected} onClick={onSelect}><IcInfo /> {selected ? 'Showing details' : 'Details'}</button>
        <CheckDriverButton trip={trip.tripNumber} />
        <ReportBreakdown trip={trip.tripNumber} truck={trip.powerUnit} trailer={trip.trailer} hasOpen={!!trip.breakdownOpen} />
        <BreakdownButton trip={trip.tripNumber} active={!!(trip.breakdown && trip.breakdown.on)} onChanged={onChanged} />
        <FinishButton trip={trip} hint={trip.finishHint} onChanged={onChanged} />
        <button type="button" className="tmw-btn tmw-btn-sm" onClick={onToggleAll}><IcExpand /> {allOpen ? 'Collapse all' : 'Expand all'}</button>
      </div>
    </div>
  );
}

// One form for everything on an OC load: carrier, solo/team, TK #, trailer #
// and drivers. Pre-filled from the trip sheet / TruckMate; anything can be fixed.
function OcForm({ trip, oc, onChanged, onError, onDone }) {
  const [f, setF] = useState({
    carrierName: oc ? oc.carrierName === 'Carrier not identified' ? '' : oc.carrierName : '',
    carrierPhone: (oc && oc.dispatchPhone) || '', carrierEmail: (oc && oc.email) || '', mc: (oc && oc.mc) || '', dot: (oc && oc.dot) || '',
    crew: (oc && oc.crew) || 'solo',
    truck: (oc && oc.truck) || '', trailer: (oc && oc.trailer) || trip.trailer || '',
    driverName: (oc && oc.driverName) || '', driverPhone: (oc && oc.driverPhone) || '',
    driver2Name: (oc && oc.driver2Name) || '', driver2Phone: (oc && oc.driver2Phone) || '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async (body) => {
    setBusy(true);
    try { await markOutsideCarrier(trip.tripNumber, body); if (onChanged) onChanged(); if (onDone) onDone(); }
    catch (e) { onError(e.message || 'Could not save.'); } finally { setBusy(false); }
  };
  const team = f.crew === 'team';
  return (
    <form className="tmw-form" onSubmit={(e) => { e.preventDefault(); if (f.carrierName.trim()) save(f); }}>
      <fieldset className="tmw-fieldset"><legend>Carrier information</legend>
        <label>Carrier name<input value={f.carrierName} onChange={set('carrierName')} required placeholder="e.g. Zeal Xpress Inc" /></label>
        <label>Carrier main phone<input value={f.carrierPhone} onChange={set('carrierPhone')} inputMode="tel" placeholder="e.g. 305-555-0147" /></label>
        <label>Carrier email<input value={f.carrierEmail} onChange={set('carrierEmail')} inputMode="email" /></label>
        <label>MC #<input value={f.mc} onChange={set('mc')} /></label>
        <label>DOT #<input value={f.dot} onChange={set('dot')} /></label>
      </fieldset>
      <fieldset className="tmw-fieldset"><legend>Truck &amp; drivers</legend>
        <label>Solo or team
          <select value={f.crew} onChange={set('crew')} aria-label="Solo or team">
            <option value="solo">Solo — 1 driver</option>
            <option value="team">Team — 2 drivers</option>
          </select>
        </label>
        <label>TK #<input value={f.truck} onChange={set('truck')} /></label>
        <label>Trailer #<input value={f.trailer} onChange={set('trailer')} /></label>
        {/* each driver on one row: name and phone side by side */}
        <div className="tmw-form-pair">
          <label>{team ? 'Driver 1 name' : 'Driver name'}<input value={f.driverName} onChange={set('driverName')} /></label>
          <label>{team ? 'Driver 1 phone' : 'Driver phone'}<input value={f.driverPhone} onChange={set('driverPhone')} inputMode="tel" /></label>
        </div>
        {team && (
          <div className="tmw-form-pair">
            <label>Driver 2 name<input value={f.driver2Name} onChange={set('driver2Name')} /></label>
            <label>Driver 2 phone<input value={f.driver2Phone} onChange={set('driver2Phone')} inputMode="tel" /></label>
          </div>
        )}
      </fieldset>
      <div className="tmw-actions">
        <button type="submit" className="tmw-btn tmw-btn-sm tmw-btn-primary" disabled={busy}>Save outside carrier</button>
        {oc && <button type="button" className="tmw-btn tmw-btn-sm" disabled={busy} onClick={() => save({ clear: true })}>Not an outside carrier</button>}
        {onDone && <button type="button" className="tmw-btn tmw-btn-sm" onClick={onDone}>Cancel</button>}
      </div>
    </form>
  );
}

// A driver's name with the phone number they gave, always right under it.
export function Person({ name, phone, trip, which = 1, callOnly = false, blocked = null }) {
  return (
    <span className="tmw-person">
      <b>{name || 'Name not provided'}</b>
      <span className="tmw-person-phone">{phone ? <><a href={`tel:${phone}`}>{phoneLabel(phone)}</a> <CallButton phone={phone} trip={trip} label={name || 'driver'} />{!callOnly && <> <TextButton trip={trip} which={which} name={name} phone={phone} blocked={blocked} /> <AiCallButton trip={trip} which={which} name={name} phone={phone} /></>}</> : 'Phone not provided'}</span>
    </span>
  );
}

// "The driver agreed to texts about this load" — the consent record carriers
// can ask for (who recorded it and when). Texts to the driver need it.
// The opt-in script the dispatcher reads on the phone, and where the driver stands.
function OptInScript({ consent, optedOut }) {
  const [open, setOpen] = useState(!consent);
  return (
    <div className="tmw-optin">
      {optedOut
        ? <div className="tmw-conflict">Replied STOP on {shortDate(Date.parse(optedOut))} — no texts can go to this number until they text START.</div>
        : consent
          ? <div className="tmw-small">{consent.confirmSentAt ? `✓ Confirmation text sent ${shortDate(Date.parse(consent.confirmSentAt))}` : 'Confirmation text goes out automatically as soon as company texting is live.'}</div>
          : null}
      <button type="button" className="tmw-link" aria-expanded={open} onClick={() => setOpen((v) => !v)}>{open ? 'Hide the opt-in script' : '📋 Opt-in script to read to the driver'}</button>
      {open && <blockquote className="tmw-script">“{OPTIN_SCRIPT}”</blockquote>}
    </div>
  );
}

function ConsentBox({ trip, oc, onChanged, onError }) {
  const [busy, setBusy] = useState(false);
  const c = oc.smsConsent;
  const toggle = async (agreed) => {
    if (agreed && !window.confirm(`Did you read the opt-in script to ${oc.driverName || 'the driver'} and did they say YES to texts?\n\nThis records your name and the time as proof.`)) return;
    setBusy(true);
    try { await setSmsConsent(trip.tripNumber, agreed); if (onChanged) onChanged(); }
    catch (e) { onError(e.message || 'Could not save.'); } finally { setBusy(false); }
  };
  const rec = oc.driverPhone ? (trip.driverConsent || {})[String(oc.driverPhone).replace(/\D+/g, '').slice(-10)] : null;
  return (
    <div>
      <label className="tmw-consent">
        <input type="checkbox" checked={!!c} disabled={busy} onChange={(e) => toggle(e.target.checked)} />
        <span>Driver agreed to texts about this load{c ? <> · <b>recorded by {c.by}</b> · {shortDate(Date.parse(c.at))}</> : ' — read the script on the phone, then check this'}</span>
      </label>
      <OptInScript consent={rec && !rec.optedOut ? rec : (c ? {} : null)} optedOut={rec && rec.optedOut} />
    </div>
  );
}

// Driver tracking link: the OC driver opens it in the TagAlong app and shares
// location until delivery. Texting is always a dispatcher click — never automatic.
function DriverLinkBox({ trip, oc, onChanged, onError }) {
  const [busy, setBusy] = useState('');
  const [copied, setCopied] = useState(false);
  const link = oc.link;
  const run = async (id, fn) => {
    setBusy(id);
    try { await fn(); if (onChanged) onChanged(); } catch (ex) { onError(ex.message || 'Could not update the tracking link.'); } finally { setBusy(''); }
  };
  const create = () => run('create', () => createDriverLink(trip.tripNumber));
  const text = () => {
    if (!oc.smsConsent) { onError('Check “Driver agreed to texts” first.'); return; }
    if (!oc.driverPhone) { onError('Add the driver’s phone first (Edit carrier / truck / driver).'); return; }
    if (!window.confirm(`Text the tracking link to ${oc.driverPhone} from your RingCentral number?`)) return;
    run('text', () => textDriverLink(trip.tripNumber));
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(link.message); setCopied(true); setTimeout(() => setCopied(false), 2500); }
    catch { window.prompt('Copy this message:', link.message); }
    run('copy', () => markDriverLinkSent(trip.tripNumber, 'Copied message'));
  };
  const off = () => { if (window.confirm('Turn off this tracking link? The driver’s app stops sharing.')) run('off', () => revokeDriverLink(trip.tripNumber)); };
  return (
    <div className="tmw-dlink" aria-label="Driver tracking link">
      <div className="tmw-label">Driver tracking link (TagAlong app)</div>
      {!link || !link.live ? (
        <>
          {link && <div className="tmw-note">Last link: {link.label}{link.lastFix ? ` · last location ${link.lastFix}` : ''}.</div>}
          <div className="tmw-note">Create a private link for this load. The driver opens it in the TagAlong app and shares location until delivery — it shows on the map like a company truck.</div>
          <div className="tmw-actions"><button type="button" className="tmw-btn tmw-btn-sm tmw-btn-primary" disabled={!!busy} onClick={create}>{busy === 'create' ? 'Creating…' : 'Create tracking link'}</button></div>
        </>
      ) : (
        <>
          <div className="tmw-dlink-status"><span className="tmw-chip" data-tone={link.tone}>{link.label}</span>{link.lastFix && <span className="tmw-small">Last location {link.lastFix}</span>}</div>
          <dl className="tmw-dl">
            <div><dt>Link</dt><dd><a href={link.url} target="_blank" rel="noreferrer" className="tmw-mono">{link.url.replace(/^https?:\/\//, '')}</a></dd></div>
            <div><dt>Load code</dt><dd className="numeric">{link.code}</dd></div>
            <div><dt>Sent</dt><dd>{link.sentLabel}</dd></div>
            <div><dt>Opened</dt><dd>{link.openedLabel}</dd></div>
          </dl>
          <div className="tmw-actions">
            <button type="button" className="tmw-btn tmw-btn-sm tmw-btn-primary" disabled={!!busy || !oc.smsConsent} title={oc.smsConsent ? '' : 'Record the driver’s consent first'} onClick={text}>{busy === 'text' ? 'Texting…' : 'Text to driver (RingCentral)'}</button>
            <button type="button" className="tmw-btn tmw-btn-sm" disabled={!!busy || !oc.smsConsent} onClick={() => { if (window.confirm(`Text ${oc.driverPhone} asking for the signed POD and BOL (with the upload link)?`)) run('pod', () => textDriver(trip.tripNumber, { kind: 'pod-request' })); }}>{busy === 'pod' ? 'Texting…' : 'Text: send POD / BOL'}</button>
            <button type="button" className="tmw-btn tmw-btn-sm" disabled={!!busy} onClick={copy}>{copied ? 'Copied ✓' : 'Copy message'}</button>
            <button type="button" className="tmw-btn tmw-btn-sm" disabled={!!busy} onClick={() => { if (window.confirm('Make a new link? The old one stops working.')) create(); }}>New link</button>
            <button type="button" className="tmw-btn tmw-btn-sm" disabled={!!busy} onClick={off}>Turn off</button>
          </div>
          {link.live && link.openedAt && !link.viaApp && <div className="tmw-source">Opened in a browser — it only tracks while the page stays open. Ask the driver to install TagAlong to keep tracking with the phone locked.</div>}
        </>
      )}
    </div>
  );
}

// Chat with the OC driver in the TagAlong app — the driver gets a push notification.
function OcChat({ trip, onError }) {
  const [list, setList] = useState(null);
  const [app, setApp] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { try { const r = await getOcMessages(trip.tripNumber); setList(r.messages || []); setApp(!!r.app); } catch { setList([]); } }, [trip.tripNumber]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { await sendOcMessage(trip.tripNumber, text.trim()); setText(''); load(); } catch (ex) { onError(ex.message || 'Could not send.'); } finally { setBusy(false); }
  };
  if (!trip.driverLink || ['completed', 'revoked', 'expired'].includes(trip.driverLink.status)) return null;
  return (
    <div className="tmw-dlink" aria-label="Messages with the driver">
      <div className="tmw-label">Messages with the driver (TagAlong app){app ? ' · notifications on' : ' · app not installed yet — shows when the link is opened'}</div>
      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
        {list && !list.length && <div className="tmw-note">No messages yet.</div>}
        {(list || []).map((m) => (
          <div key={m.id} className="tmw-small" style={{ margin: '4px 0', textAlign: m.from === 'driver' ? 'left' : 'right' }}>
            <b>{m.from === 'driver' ? (m.by || 'Driver') : (m.by || 'Dispatch')}</b> · {shortDate(Date.parse(m.at))}{m.from === 'dispatch' && m.readAt ? ' · read' : ''}<br />
            {m.text}{(m.docIds || []).length ? ` 📷 ${m.docIds.length} photo${m.docIds.length === 1 ? '' : 's'} (in Documents)` : ''}
          </div>
        ))}
      </div>
      <form className="tmw-form tmw-form-inline" onSubmit={send}>
        <label>Message<input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="e.g. Call the receiver 1 hour before arrival" /></label>
        <button type="submit" className="tmw-btn tmw-btn-sm tmw-btn-primary" disabled={busy || !text.trim()}>{busy ? 'Sending…' : 'Send to driver'}</button>
      </form>
    </div>
  );
}

function CarrierBody({ trip, oc, onChanged, onError }) {
  const [ck, setCk] = useState({ location: '', note: '', issue: false });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [history, setHistory] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!ck.location.trim() && !ck.note.trim()) return;
    setBusy(true);
    try { await logCheckin(trip.tripNumber, ck); setCk({ location: '', note: '', issue: false }); if (onChanged) onChanged(); }
    catch (ex) { onError(ex.message || 'Could not log the check-in.'); } finally { setBusy(false); }
  };
  // which values came straight off the uploaded trip sheet (vs typed by dispatch / the driver)
  const sheet = (trip.manifest && trip.manifest.outsideCarrier) || {};
  const fromSheet = (k, v) => (v && sheet[k] && String(sheet[k]).trim() === String(v).trim() ? <span className="tmw-from"> · from trip sheet</span> : null);
  return (
    <section aria-label={`Outside carrier for trip ${trip.tripNumber}`}>
      <div className="tmw-actions" style={{ marginTop: 0 }}>
        <button type="button" className="tmw-btn tmw-btn-sm tmw-btn-primary" aria-expanded={editing} onClick={() => setEditing((v) => !v)}>{editing ? 'Close editing' : 'Edit carrier, truck & drivers'}</button>
        <span className="tmw-small">Filled in from the trip sheet — edit anything that came through wrong or changed.</span>
      </div>
      {editing && <OcForm trip={trip} oc={oc} onChanged={onChanged} onError={onError} onDone={() => setEditing(false)} />}
      <div className="tmw-oc-block" aria-label="Carrier information">
        <div className="tmw-label">Carrier information</div>
        <dl className="tmw-dl">
          <div><dt>Carrier name</dt><dd>{oc.carrierName}{oc.unsaved ? ' (from trip sheet)' : ''}</dd></div>
          <div><dt>Main phone</dt><dd>{oc.dispatchPhone ? <><a href={`tel:${oc.dispatchPhone}`}>{oc.dispatchPhone}</a> <CallButton phone={oc.dispatchPhone} trip={trip.tripNumber} label={`${oc.carrierName} dispatch`} /></> : 'Not on file — add it with Edit'}</dd></div>
          <div><dt>Email</dt><dd>{oc.email || NOT_REPORTED}</dd></div>
          <div><dt>MC / DOT</dt><dd>{[oc.mc && `MC ${oc.mc}`, oc.dot && `DOT ${oc.dot}`].filter(Boolean).join(' · ') || NOT_REPORTED}</dd></div>
          <div><dt>Tracking</dt><dd>{oc.method}{oc.trackingEmail ? ` · ${oc.trackingEmail}` : ''}</dd></div>
          <div><dt>Marked OC by</dt><dd>{oc.source}{oc.evidence ? ` — “${oc.evidence}”` : ''}</dd></div>
        </dl>
      </div>
      <div className="tmw-oc-block" aria-label="Truck and drivers">
        <div className="tmw-label">Truck & drivers · {oc.crew === 'team' ? 'Team — 2 drivers' : 'Solo — 1 driver'}</div>
        <dl className="tmw-dl">
          <div><dt>TK #</dt><dd>{oc.truck || 'Not provided'}{fromSheet('truck', oc.truck)}</dd></div>
          <div><dt>Trailer #</dt><dd>{oc.trailer || 'Not provided'}{fromSheet('trailer', oc.trailer)}</dd></div>
          <div><dt>{oc.crew === 'team' ? 'Driver 1' : 'Driver'}</dt><dd><Person name={oc.driverName} phone={oc.driverPhone} trip={trip.tripNumber} />{fromSheet('driverName', oc.driverName)}<DriverThread phone={oc.driverPhone} name={oc.driverName} /></dd></div>
          {oc.crew === 'team' && <div><dt>Driver 2</dt><dd><Person name={oc.driver2Name} phone={oc.driver2Phone} trip={trip.tripNumber} which={2} /><DriverThread phone={oc.driver2Phone} name={oc.driver2Name} /></dd></div>}
          <div><dt>ELD / engine data</dt><dd>Carrier's ELD — not shared with us</dd></div>
        </dl>
      </div>
      <ConsentBox trip={trip} oc={oc} onChanged={onChanged} onError={onError} />
      {oc.missing.length > 0
        ? <div className="tmw-conflict" role="status"><b>Driver info missing:</b> {oc.missing.join(', ')}. Required for every {oc.crew} OC load — the tracking link makes the driver fill it in before sharing.</div>
        : oc.infoFrom && <div className="tmw-source">Driver info confirmed by {oc.infoFrom.by} · {ago(oc.infoFrom.at)}</div>}
      <DriverLinkBox trip={trip} oc={oc} onChanged={onChanged} onError={onError} />
      <OcChat trip={trip} onError={onError} />
      <div className="tmw-actions">
        <button type="button" className="tmw-btn tmw-btn-sm" aria-expanded={history} onClick={() => setHistory((v) => !v)}>{history ? 'Hide trip history' : 'Trip history — trail & stops'}</button>
      </div>
      {history && <Suspense fallback={<div className="tmw-note">Loading map…</div>}><OcHistory trip={trip} /></Suspense>}

      <div className="tmw-label" style={{ marginTop: 14 }}>Check-ins</div>
      <form className="tmw-form tmw-form-inline" onSubmit={submit}>
        <label>Where is the truck?<input value={ck.location} onChange={(e) => setCk((x) => ({ ...x, location: e.target.value }))} placeholder="e.g. I-95 near Richmond, VA" /></label>
        <label>Note<input value={ck.note} onChange={(e) => setCk((x) => ({ ...x, note: e.target.value }))} placeholder="e.g. Unloading, ETA 6 PM" /></label>
        <label className="tmw-check-inline"><input type="checkbox" checked={ck.issue} onChange={(e) => setCk((x) => ({ ...x, issue: e.target.checked }))} /> Problem / delay</label>
        <button type="submit" className="tmw-btn tmw-btn-sm tmw-btn-primary" disabled={busy}>Log check call</button>
      </form>
      {oc.checkins.length === 0 && <div className="tmw-note">No check-ins yet. Call the carrier and log where the truck is.</div>}
      <ul className="tmw-checkins">
        {oc.checkins.slice(0, 12).map((c, i) => (
          <li key={i} data-issue={c.issue || undefined}>
            <b>{c.source === 'email' ? 'Email' : c.source === 'check call' ? 'Check call' : c.source}</b> · {shortDate(Date.parse(c.at))}{c.from ? ` · ${c.from}` : ''}{c.by ? ` · ${c.by}` : ''}
            <div>{c.location ? `${c.location} — ` : ''}{c.text}{c.issue ? ' (problem)' : ''}</div>
          </li>
        ))}
      </ul>
    </section>
  );
}

// A company driver's name, phone and ID (Samsara / TruckMate) + how dispatch reaches them:
// phone with Call, texts consent (per driver, carries across loads), POD/BOL ask.
function DriverProfile({ trip, d, onChanged, onError }) {
  const [busy, setBusy] = useState('');
  const i = d.info;
  if (!i) {
    return (
      <div className="tmw-driver-profile tmw-small">
        No Samsara profile matched for this driver.
        {d.phone && <div className="tmw-cd-phone"><a href={`tel:${d.phone}`}>{d.phone}</a><span className="tmw-from"> · from trip sheet</span> <CallButton phone={d.phone} trip={trip.tripNumber} label={d.name || 'driver'} /><span className="tmw-cd-consent">Texts and AI calls need a Samsara profile</span></div>}
      </div>
    );
  }
  const run = async (id, fn) => { setBusy(id); try { await fn(); if (onChanged) onChanged(); } catch (e) { onError(e.message || 'Could not update.'); } finally { setBusy(''); } };
  const consent = (agreed) => {
    if (agreed && !window.confirm(`Did you read the opt-in script to ${i.name || 'the driver'} and did they say YES to texts?\n\nThis records your name and the time as proof.`)) return;
    run('consent', () => setDriverConsent(i.phone, i.name, agreed));
  };
  const rows = [
    ['Name', i.name || d.name],
    ['Phone', i.phone ? <><a href={`tel:${i.phone}`}>{i.phone}</a> <CallButton phone={i.phone} trip={trip.tripNumber} label={i.name || 'driver'} /> <TextButton trip={trip.tripNumber} which={d.which} name={i.name || d.name} phone={i.phone} blocked={textBlocked(d)} /> <AiCallButton trip={trip.tripNumber} which={d.which} name={i.name || d.name} phone={i.phone} /></> : 'Not in Samsara — add it to the driver profile'],
    ['Driver ID', d.code || i.username],
  ].filter(([, v]) => v != null && v !== '');
  return (
    <div className="tmw-driver-profile">
      <dl className="tmw-dl">{rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
      {d.sheetPhoneDiffers && <div className="tmw-small">Trip sheet lists a different number: <a href={`tel:${d.sheetPhoneDiffers}`}>{d.sheetPhoneDiffers}</a></div>}
      {i.phone && (
        <>
          <label className="tmw-consent">
            <input type="checkbox" checked={!!d.consent} disabled={busy === 'consent'} onChange={(e) => consent(e.target.checked)} />
            <span>{i.name || 'Driver'} agreed to work texts{d.consent ? <> · <b>recorded by {d.consent.by}</b> · {shortDate(Date.parse(d.consent.at))}</> : ' — read the script once, then check this (applies to all their loads)'}</span>
          </label>
          <OptInScript consent={d.consent} optedOut={d.optedOut} />
          <DriverThread phone={i.phone} name={i.name || d.name} />
          <div className="tmw-actions">
            <button type="button" className="tmw-btn tmw-btn-sm" disabled={!d.consent || !!busy} title={d.consent ? '' : 'Record the driver’s consent first'}
              onClick={() => { if (window.confirm(`Text ${i.phone} asking for the signed POD and BOL for load ${trip.tripNumber} (with an upload link)?`)) run('pod', () => textDriver(trip.tripNumber, { kind: 'pod-request', driver: d.which })); }}>
              {busy === 'pod' ? 'Texting…' : 'Text: send POD / BOL'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function CrewBody({ trip, onChanged, onError }) {
  const c = crewModel(trip);
  const [marking, setMarking] = useState(false);
  // Only offered when nothing from our own fleet (Samsara / FMC00A) reports
  // for this truck — a company truck is never an outside carrier.
  const ownFleet = !!(trip.live && trip.live.lat != null && trip.live.source && trip.live.source !== 'driver app');
  const markOc = ownFleet ? null : marking
    ? <OcForm trip={trip} oc={null} onChanged={onChanged} onError={onError} onDone={() => setMarking(false)} />
    : <button type="button" className="tmw-link" style={{ marginTop: 8 }} onClick={() => setMarking(true)}>Not a company truck? Mark as outside carrier (OC)…</button>;
  return (
    <section className="tmw-crew" aria-label={`Driver crew for truck ${trip.powerUnit || ''}`}>
      <div className="tmw-wheel">At the wheel <b>{c.atWheel.verified ? c.atWheel.name : c.atWheel.note}</b>{c.atWheel.verified && <span className="tmw-chip" data-tone="ok">Driving</span>}</div>
      {c.drivers.length === 0 && <div className="tmw-note">No driver assigned in TruckMate.</div>}
      {c.drivers.map((d) => (
        <div className="tmw-driver" key={d.role}>
          <div>
            <strong>{d.name}</strong>
            <div className="tmw-small">{d.role}{d.code ? ` · ID ${d.code}` : ''} · {d.status}{d.stale && d.statusKey ? ' (stale)' : ''}{!d.matched ? ' · not matched in Samsara' : ''}</div>
          </div>
          <div><span className="tmw-label">Shift left</span><b>{d.shiftLeft}</b></div>
          <div><span className="tmw-label">Drive left</span><b>{d.driveLeft}</b></div>
          <div><span className="tmw-label">Cycle left</span><b>{d.cycleLeft || NOT_REPORTED}</b></div>
          <DriverProfile trip={trip} d={d} onChanged={onChanged} onError={onError} />
        </div>
      ))}
      {c.conflicts.map((x, i) => <div className="tmw-conflict" key={i}>Source conflict: {x}</div>)}
      <div className="tmw-source">{c.source}</div>
      {markOc}
    </section>
  );
}

function HealthBody({ trip, health: h, isOpen, setOpen }) {
  const k = `${trip.tripNumber}:telematics`;
  return (
    <section aria-label={`Truck health for ${trip.powerUnit || ''}`}>
      <div className="tmw-overview">
        {h.summary.map((s) => <div key={s.label}><span className="tmw-label">{s.label}</span><b>{s.value}</b></div>)}
      </div>
      {h.faults.length > 0 && (
        <div className="tmw-alert">
          <strong>Engine fault{h.faults.length === 1 ? '' : 's'} reported</strong>
          {h.faults.slice(0, 5).map((f) => <div key={f.code}>{f.code} — {f.meaning || 'No description from the provider'}</div>)}
          <div className="tmw-small">Review with maintenance; the code alone is not a diagnosis.</div>
        </div>
      )}
      <Disclosure id={k} isOpen={isOpen} setOpen={setOpen} className="tmw-disclosure" summary={<>All telematics <span className="tmw-small">· 6 sections</span></>}>
        <div className="tmw-groups">
          {h.groups.map((g) => (
            <Disclosure key={g.title} id={`${k}:${g.title}`} isOpen={isOpen} setOpen={setOpen} className="tmw-group" summary={g.title}>
              <dl className="tmw-dl">
                {g.rows.map((r, i) => <div key={`${r.label}-${i}`}><dt>{r.label}</dt><dd data-missing={r.missing}>{r.value}</dd></div>)}
              </dl>
            </Disclosure>
          ))}
        </div>
      </Disclosure>
      <div className="tmw-source">{h.source}</div>
    </section>
  );
}

// TruckMate's delivery time as entered ("2026-10-21T04:37:19" → "Oct 21, 4:37 AM"). Pure.
export const tmWall = (x) => { const d = new Date(`${String(x).slice(0, 19)}Z`); return Number.isNaN(d.getTime()) ? String(x) : d.toLocaleString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); };
// "BROUSSARD, LA, 70518" → { city, state, key: "BROUSSARD|LA" } — the same key the server uses. Pure.
export function commitPlace(place) {
  const p = String(place || '').split(',').map((x) => x.trim());
  const city = String(p[0] || '').toUpperCase().replace(/[^A-Z ]/g, '').replace(/\s+/g, ' ').trim();
  const state = String(p[1] || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
  return city && state ? { city, state, key: `${city}|${state}` } : null;
}
// "2026-10-23T08:00" → "Fri, Oct 23, 8:00 AM" (the stop's own local time, as typed). Pure.
export const commitLabel = (local) => { const d = new Date(`${local}:00Z`); return Number.isNaN(d.getTime()) ? local : d.toLocaleString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); };

// 📅 the date + time we committed to the customer for this stop. Jarvis treats it as the stop's appointment,
// and alerts dispatch (email + text) if the truck is going to be more than an hour late.
export function CommitTime({ s, trip, onError, onChanged }) {
  const where = commitPlace(s.place);
  const raw = where && trip && trip.commitments ? trip.commitments[where.key] : null;
  const saved = raw && raw.local ? raw : null;            // a cleared stop has no time
  const fcfs = raw && raw.fcfs ? raw : null;              // rate con: first come, first served — no appointment
  const [cur, setCur] = useState(saved || null);
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setCur(saved || null); }, [saved]);
  if (!where || !trip) return null;
  // saving also remembers it for this customer + location's next outbound (forget: stop that)
  const save = async (local, forget = false) => {
    setBusy(true);
    try {
      const loadDate = (trip.manifest && trip.manifest.dateLoaded) || null;
      const out = await setStopCommitment(trip.tripNumber, { city: where.city, state: where.state, customer: s.customer, place: `${where.city}, ${where.state}`, local, ...(loadDate ? { loadDate } : {}), ...(forget ? { forget: true } : {}) });
      const x = out && out[where.key];
      setCur(x && x.local ? x : null); setOpen(false);
      if (onChanged) onChanged();
    } catch (e) { onError(e.message || 'Could not save the committed time.'); } finally { setBusy(false); }
  };
  return (
    <div className="tmw-commit">
      {fcfs && !open && (!cur || cur.fcfs) && (
        <div className="tmw-meta"><b>FCFS — first come, first served</b> · no appointment needed{fcfs.day ? ` · ${new Date(`${fcfs.day}T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' })}` : ''}{fcfs.hours ? <> · receiving <b>{fcfs.hours}</b> — arrive before they close</> : ''} <span className="tmw-small">· from the rate con</span></div>
      )}
      {cur && !cur.fcfs && !open && (
        <div className="tmw-kv"><span>Committed to customer</span><b>{commitLabel(cur.local)} <span className="tmw-small">local time{cur.fromRC ? ` · rate con delivery appointment${cur.rule && cur.rule !== 'the delivery appointment on the rate con' ? ` (${cur.rule})` : ''}` : cur.fromTM ? ' · TruckMate delivery appointment' : cur.fromSheet ? ` · from the trip sheet${cur.rule ? ` (${cur.rule})` : ''}` : cur.auto ? ` · saved from a previous load${cur.rule ? ` (${cur.rule})` : ''}` : cur.by ? ` · set by ${cur.by}` : ''}</span></b></div>
      )}
      {cur && !cur.fcfs && !open && /handwritten/i.test(cur.rule || '') && (
        <div className="tmw-stop-note" data-tone="warning"><IcAlertRound size={18} /><span>Commitment interpreted from handwritten trip sheet. Verify with customer.</span></div>
      )}
      {open ? (
        <div className="tmw-commit-edit">
          <input type="datetime-local" value={val} onChange={(e) => setVal(e.target.value)} aria-label={`Committed date and time for ${s.customer || where.city}`} />
          <span className="tmw-small">{where.city}, {where.state} local time</span>
          <button type="button" className="tmw-btn tmw-btn-sm tmw-btn-primary" disabled={busy || !val} onClick={() => save(val)}>{busy ? 'Saving…' : 'Save'}</button>
          {cur && <button type="button" className="tmw-btn tmw-btn-sm" disabled={busy} onClick={() => save(null)} title="Clear it on this load only">Clear</button>}
          {s.customer && <button type="button" className="tmw-btn tmw-btn-sm" disabled={busy} onClick={() => save(null, true)} title="Clear it here and stop filling it in on this customer's next loads">Don't use on future loads</button>}
          <button type="button" className="tmw-btn tmw-btn-sm" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
        </div>
      ) : (
        <button type="button" className="tmw-btn tmw-btn-sm tmw-commit-btn" onClick={() => { setVal(cur ? cur.local : ''); setOpen(true); }} title="The date and time we committed to the customer — Jarvis alerts dispatch if the truck will be more than an hour late">
          <IcCalendar /> {cur ? 'Change committed time' : 'Add committed time'}
        </button>
      )}
    </div>
  );
}

export function StopRow({ s, trip, oc, eta, onError, onChanged }) {
  const [asking, setAsking] = useState(false);
  const contact = trip ? contactFor(trip) : null;
  const ask = async () => {
    const label = `stop ${s.number != null ? s.number : ''} (${[s.customer, s.place].filter(Boolean).join(', ')})`.replace('stop  (', 'your stop (');
    if (!window.confirm(`Text ${contact.name} (${contact.phone}): “please confirm ${label} on load ${trip.tripNumber} was delivered — reply YES”?`)) return;
    setAsking(true);
    try { await textDriver(trip.tripNumber, { kind: 'confirm-stop', stopKey: s.key, stopLabel: label }); if (onChanged) onChanged(); }
    catch (e) { onError(e.message || 'Could not text the driver.'); } finally { setAsking(false); }
  };
  const ev = s.visit;
  return (
    <li className="tmw-stop" data-status={s.status} data-action={s.action} data-sub={s.subStop || undefined}>
      <div className="tmw-stop-head">
        <div className="tmw-stop-no" aria-hidden="true">{s.status === 'completed' || s.tmDelivered ? '✓' : (s.subStop ? '+' : (s.number != null ? s.number : '·'))}</div>
        <div className="tmw-stop-who">
          <div className="tmw-stop-kind"><span aria-hidden="true">{s.action === 'Pickup' ? '↑ ' : s.action === 'Delivery' ? '↓ ' : ''}</span>{s.action}{s.subStop ? ' · same stop' : ''}{s.number != null ? ` · Stop ${s.number}` : ''}</div>
          <strong>{s.customer || 'Customer not provided'}</strong>
          <div className="tmw-meta">{s.address ? `${s.address}, ` : ''}{s.place || 'Address not provided'}</div>
        </div>
        <div className="tmw-stop-state"><b>{s.statusLabel}</b></div>
      </div>
      <div className="tmw-stop-body">
        <div className="tmw-stop-sec">
          <div className="tmw-stop-h"><IcClock />Timing</div>
          <div className="tmw-stop-timing">
            <div className="tmw-kv"><span>Appointment</span><b>{s.appointment ? friendlyAppt(s.appointment) : (s.window ? `Window ${s.window}` : NOT_REPORTED)}</b></div>
            {(s.appointment || s.window) && <div className="tmw-kv"><span>Source</span><b>{s.appointment ? `${s.appointmentSource === 'handwritten' ? 'Handwritten' : s.appointmentSource === 'rate con' ? 'Rate confirmation' : 'Trip sheet'} · timezone unstated` : 'TruckMate'}</b></div>}
            {s.action === 'Delivery' && s.status !== 'completed' && !s.tmDelivered && <CommitTime s={s} trip={trip} onError={onError} onChanged={onChanged} />}
            {eta && eta.passed && <div className="tmw-meta">Truck drove past — most likely delivered</div>}
            {eta && eta.etaMs != null && <div className="tmw-kv"><span>ETA (estimated)</span><b>{stopLocalTime(eta.etaMs, s.place)}</b></div>}
            {eta && eta.etaMs != null && eta.miles != null && <div className="tmw-kv"><span>Distance</span><b>{eta.miles.toLocaleString()} mi{eta.legMiles != null ? <span className="tmw-small"> · {eta.legMiles.toLocaleString()} mi from the stop before</span> : null}</b></div>}
            {eta && eta.etaMs != null && eta.apptFrom === 'committed' && eta.apptMs != null && (eta.etaMs - eta.apptMs > 60 * 60000
              ? <div className="tmw-stop-note tmw-late" data-tone="critical"><IcAlertRound size={18} /><span>Estimated delay: ~{Math.round((eta.etaMs - eta.apptMs) / 3600000 * 10) / 10} h past the committed time{s.appointment ? ' · verify appointment timezone' : ''}</span></div>
              : <div className="tmw-small tmw-ontime">On time for the committed time</div>)}
            {s.action === 'Delivery' && <ArtisStop s={s} trip={trip} part="eta" oursMs={eta && eta.etaMs ? eta.etaMs : null} onError={onError} onChanged={onChanged} />}
          </div>
        </div>
        <div className="tmw-stop-sec">
          {s.receivingHours && (
            <div className="tmw-stop-block">
              <div className="tmw-stop-h"><IcClock />Receiving hours</div>
              {hoursRows(s.receivingHours)
                ? hoursRows(s.receivingHours).map(([d, h]) => <div className="tmw-kv" key={d}><span>{d}</span><b>{h}</b></div>)
                : <div className="tmw-meta"><b>{s.receivingHours}</b></div>}
            </div>
          )}
          {(s.quantity || s.bills.length > 0) && (
            <div className="tmw-stop-block">
              <div className="tmw-stop-h"><IcBox />Cargo</div>
              <div className="tmw-stop-cargo">
                {s.quantity && <b>{s.quantity}</b>}
                {s.bills.length > 0 && (
                  <details className="tmw-bills">
                    <summary>Bills <small>{s.bills.length}</small></summary>
                    <div className="tmw-meta"><b className="numeric">{s.bills.join(', ')}</b>{s.billsShared ? ' (all bills for this city — shared with another stop)' : ''}</div>
                  </details>
                )}
              </div>

            </div>
          )}
        </div>
        {s.references.length > 0 && <div className="tmw-stop-sec tmw-stop-wide"><div className="tmw-stop-h"><IcDoc />References</div><div className="tmw-tags">{s.references.map((r, i) => <span className="tmw-tag" key={i}>{r}</span>)}</div></div>}
        {(s.callAhead.length > 0 || s.instructions.length > 0) && (
          <div className="tmw-stop-sec tmw-stop-wide">
            <div className="tmw-stop-h"><IcInfo />{s.instructions.some((n) => /before leaving/i.test(n)) ? 'Before leaving' : 'Instructions'}</div>
            {s.callAhead.map((c, i) => <div className="tmw-stop-do" key={`c${i}`}><b>{c.method === 'text' ? 'Text' : 'Call'} ahead</b> {[c.contact, c.phone].filter(Boolean).join(' ')}{c.leadMinutes != null ? ` · ${c.leadMinutes} min before` : ''}{c.phone && <> <CallButton phone={c.phone} trip={trip && trip.tripNumber} label={c.contact || s.customer || 'call-ahead'} /></>}</div>)}
            {s.instructions.map((n) => <div className="tmw-stop-do" key={n}>{n}</div>)}
          </div>
        )}
        {s.action === 'Delivery' && <div className="tmw-stop-wide"><ArtisStop s={s} trip={trip} part="docs" heading={<div className="tmw-stop-h"><IcDoc />Documents</div>} onError={onError} onChanged={onChanged} /></div>}
        {s.handwritten.length > 0 && (
          <div className="tmw-stop-sec tmw-stop-wide">
            <div className="tmw-stop-h"><IcDoc />Source notes</div>
            <div className="tmw-tags">{s.handwritten.map((h) => <span className="tmw-tag" key={`h${h}`}>Handwritten: {h}</span>)}</div>
          </div>
        )}
        {(ev || s.tracked || s.driverConfirm || s.status === 'completed' || s.tmDelivered || (contact && contact.consent && s.action === 'Delivery' && s.status !== 'completed')) && (
          <div className="tmw-stop-sec tmw-stop-wide">
            <div className="tmw-stop-h"><IcPin />Evidence</div>
            {s.tracked && <div className="tmw-small">Geofence-tracked</div>}
            {s.driverConfirm
              ? <div className="tmw-small">Driver replied “{String(s.driverConfirm.text || 'YES').slice(0, 40)}” · {shortDate(Date.parse(s.driverConfirm.at))}</div>
              : s.status === 'completed' && <div className="tmw-small">Visit only — not proof of delivery</div>}
            {s.tmDelivered && <div className="tmw-small">TruckMate: delivered{s.deliveredAt ? ` ${tmWall(s.deliveredAt)}` : ''}</div>}
            {contact && contact.consent && s.action === 'Delivery' && s.status !== 'completed' && (
              <button type="button" className="tmw-btn tmw-btn-sm" disabled={asking} onClick={ask}>{asking ? 'Texting…' : 'Ask driver to confirm'}</button>
            )}
            {ev && (
              <details className="tmw-group">
                <summary>Geofence evidence</summary>
                <dl className="tmw-dl">
                  <div><dt>Geofence</dt><dd>{ev.geofence ? `${ev.geofence.name} (${ev.geofence.source})` : NOT_REPORTED}</dd></div>
                  <div><dt>Entered</dt><dd>{ev.enteredAt ? shortDate(ev.enteredAt) : '—'}</dd></div>
                  <div><dt>Exited</dt><dd>{ev.exitedAt ? shortDate(ev.exitedAt) : '—'}</dd></div>
                  <div><dt>Dwell</dt><dd>{ev.dwellMin != null ? `${ev.dwellMin} min` : '—'}</dd></div>
                  <div><dt>Rule</dt><dd>{ev.rule}</dd></div>
                  {(ev.evidence || []).slice(-4).map((e, i) => <div key={i}><dt>{e.event}</dt><dd>{shortDate(Date.parse(e.at))} · {e.detail}</dd></div>)}
                </dl>
              </details>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

// The rate con's pickups and deliveries on the stop list: pickup / delivery numbers, appointment, name, phone.
export function RateConStops({ rc }) {
  const m = rateConModel(rc);
  if (!m || !m.stops.length) return null;
  const when = (x) => [x.date, x.time, x.appointment && x.appointment !== x.time ? x.appointment : null].filter(Boolean).join(' · ');
  return (
    <details className="tmw-rc-stops" style={{ border: '1px solid #D4DEEA', borderRadius: 8, padding: '8px 12px', margin: '8px 0', background: '#fff' }}>
      <summary className="tmw-label">Rate confirmation{m.broker ? ` · ${m.broker}` : ''}{m.load ? ` · load ${m.load}` : ''} · Show extracted details</summary>
      {m.stops.map((x, i) => (
        <div key={`${x.kind}${i}`} className="tmw-rc-stop" data-action={x.kind}>
          <b>{x.kind}</b>{x.name ? ` · ${x.name}` : ''}{x.city || x.state ? ` · ${[x.city, x.state].filter(Boolean).join(', ')}` : ''}
          {when(x) && <> · <b>{when(x)}</b></>}
          {x.refs && <> · {x.kind === 'Pickup' ? 'PU #' : 'Ref #'} <b className="numeric">{x.refs}</b></>}
          {x.phone && <> · <a href={`tel:${String(x.phone).replace(/[^\d+]/g, '')}`}>{x.phone}</a></>}
        </div>
      ))}
      {m.references.length > 0 && <div className="tmw-small" style={{ marginTop: 6 }}>References: {m.references.map((r) => (typeof r === 'string' ? r : [r.label || r.type, r.value || r.number].filter(Boolean).join(' '))).join(' · ')}</div>}
    </details>
  );
}

function StopsBody({ trip, stops: m, docs, oc, etas, onError, onChanged }) {
  const sheet = trip.manifest;
  return (
    <div>
      <div className="tmw-doc-head">
        <span className="tmw-small">
          Order: {m.source === 'none' ? 'no stop list' : m.source}{m.verified ? '' : ' · Sequence unverified'} · Trip sheet {sheet ? `version ${sheet.version || 1}, uploaded ${ago(sheet.uploadedAt) || '—'}${sheet.uploadedBy ? ` by ${sheet.uploadedBy}` : ''}` : 'not uploaded'}
        </span>
        <DocButtons group={docs.tripsheet.latest} />
      </div>
      <RateConStops rc={trip.rateCon} />
      {docs.tripsheet.originalMissing && <div className="tmw-note">Original sheet not stored (uploaded before originals were kept). Re-upload in the Trip sheets tab to attach it.</div>}
      {m.changes && (m.changes.added.length + m.changes.removed.length + m.changes.changed.length > 0) && (
        <div className="tmw-conflict">Changed since version {m.changes.previousVersion}: {[...m.changes.added.map((x) => `added ${x}`), ...m.changes.removed.map((x) => `removed ${x}`), ...m.changes.changed].join(' · ')}</div>
      )}
      {sheet && (sheet.diffs || []).length > 0 && <div className="tmw-conflict">Trip sheet ≠ TruckMate: {sheet.diffs.map((d) => d.msg).join(' · ')}</div>}
      {sheet && sheet.pickupPlan && <div className="tmw-meta">Pickup plan (handwritten) <b>{sheet.pickupPlan}</b></div>}
      <div className="tmw-legend" aria-hidden="true"><span>✓ Visit complete</span><span>◉ At stop</span><span>○ Upcoming</span></div>
      {m.stops.length ? <ol className="tmw-stops">{m.stops.map((s) => <StopRow key={s.key} s={s} trip={trip} oc={oc} eta={s.status !== 'completed' && !s.tmDelivered ? stopEta(s, trip, etas) : null} onError={onError} onChanged={onChanged} />)}</ol> : <div className="tmw-note">No stops available. Upload the trip sheet in the Trip sheets tab.</div>}
      <div className="tmw-source">Visits come from validated Samsara address geofences (arrival, dwell, confirmed exit). A completed visit is not proof of delivery; TruckMate delivery status is shown separately.</div>
    </div>
  );
}

// rate-con state lives on the card so an upload in progress survives collapsing
function useRateCon(trip, onError, onChanged) {
  const [rc, setRc] = useState(trip.rateCon || null);
  const [checks, setChecks] = useState(trip.rcCheck || {});
  const [stage, setStage] = useState('');
  const [saving, setSaving] = useState('');
  useEffect(() => { setRc(trip.rateCon || null); }, [trip.rateCon]);
  useEffect(() => { setChecks(trip.rcCheck || {}); }, [trip.rcCheck]);
  const upload = async (files) => {
    setStage('Uploading and extracting…');
    try { const d = await uploadRateCon(trip.tripNumber, files); setRc(d); if (onChanged) onChanged(); }
    catch (ex) { onError(ex.message || 'Upload failed.'); }
    finally { setStage(''); }
  };
  const toggle = async (instruction) => {
    const done = !(checks[instruction] && checks[instruction].done);
    const prev = checks;
    setSaving(instruction);
    try { setChecks((await setRcInstruction(trip.tripNumber, instruction, done)) || {}); }
    catch { setChecks(prev); onError('Could not save the sign-off — try again.'); }
    finally { setSaving(''); }
  };
  const m = rateConModel(rc);
  const doneCount = m ? m.instructions.filter((s) => checks[s] && checks[s].done).length : 0;
  return { rc, m, checks, stage, saving, upload, toggle, doneCount };
}

function RateConBody({ docs, r }) {
  const fileRef = useRef(null);
  const { rc, m, checks, stage, saving, doneCount } = r;
  return (
    <DropZone onFiles={(files) => { if (!stage) r.upload(files); }} disabled={!!stage} label="Drop the rate con here">
      <input ref={fileRef} type="file" accept="application/pdf,image/*" multiple hidden
        onChange={(e) => { const files = Array.from((e.target && e.target.files) || []); if (files.length) r.upload(files); if (fileRef.current) fileRef.current.value = ''; }} />
      <div className="tmw-doc-head">
        <span className="tmw-small">{stage || (rc ? `Version ${rc.version || (docs.ratecon.latest && docs.ratecon.latest.version) || 1} · uploaded ${ago(rc.uploadedAt) || '—'}${rc.uploadedBy ? ` by ${rc.uploadedBy}` : ''}` : 'No rate confirmation for this trip')}</span>
        <span className="tmw-file-list">
          <DocButtons group={docs.ratecon.latest} />
          <button type="button" className="tmw-btn tmw-btn-sm" disabled={!!stage} onClick={() => fileRef.current && fileRef.current.click()}>{rc ? 'Replace' : 'Upload rate con'}</button>
        </span>
      </div>
      {docs.ratecon.originalMissing && <div className="tmw-note">Original file not stored (uploaded before originals were kept). Replace it to attach the original.</div>}
      {rc && rc.docError && <div className="tmw-conflict">{rc.docError}</div>}
      {m && (
        <>
          <div className="tmw-doc-head">
            <div><span className="tmw-label">Route</span><b>{m.route.from || NOT_REPORTED} → {m.route.to || NOT_REPORTED}</b></div>
            <div style={{ textAlign: 'right' }}><span className="tmw-label">Total rate</span><b className="numeric">{m.rate || NOT_REPORTED}</b></div>
          </div>
          <div className="tmw-rate-grid">{m.strip.map((s) => <div key={s.label}><span className="tmw-label">{s.label}</span>{s.value}</div>)}</div>
          {m.stops.length > 0 && (
            <div className="tmw-rc-stops">
              {m.stops.map((s, i) => (
                <div className="tmw-rc-stop" key={i}>
                  <div className="tmw-stop-kind">{s.kind} {i + 1}</div>
                  <strong>{s.name || 'Name not provided'}</strong>
                  <div className="tmw-meta">{[s.address, s.city, s.state, s.zip].filter(Boolean).join(', ') || 'Address not provided'}</div>
                  <div className="tmw-meta">Appointment <b>{[s.date, s.time || s.appointment].filter(Boolean).join(' ') || NOT_REPORTED}</b></div>
                  {s.refs && <div className="tmw-meta">Refs <b className="numeric">{String(s.refs)}</b></div>}
                </div>
              ))}
            </div>
          )}
          {(m.contacts.length > 0 || m.brokerPhone || m.brokerEmail) && (
            <div className="tmw-rc-contacts">
              <div className="tmw-label">Contacts on the rate con{m.broker ? ` · ${m.broker}` : ''}</div>
              <ul className="tmw-contacts">
                {(m.contacts.length ? m.contacts : [{ role: 'broker_rep', company: m.broker, phone: m.brokerPhone, email: m.brokerEmail }]).map((c, i) => (
                  <li key={i}>
                    <span className="tmw-contact-role">{{ broker_rep: 'Broker', after_hours: 'After hours', dispatch: 'Dispatch', tracking: 'Tracking', billing: 'Billing', shipper: 'Shipper', receiver: 'Receiver' }[c.role] || 'Contact'}</span>
                    <span><b>{c.name || c.company || '—'}</b>{c.name && c.company ? ` · ${c.company}` : ''}</span>
                    <span>{c.phone ? <a href={`tel:${c.phone}`}>{c.phone}</a> : null}{c.phone && c.email ? ' · ' : ''}{c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : null}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {m.handwritten.length > 0 && <div className="tmw-small"><b>Handwritten on the rate con:</b> {m.handwritten.join(' · ')}</div>}
          {m.matchedBy && <div className="tmw-source">From the uploaded packet · matched by {m.matchedBy}</div>}
          {m.instructions.length > 0 && (
            <div className="tmw-handling" data-done={doneCount === m.instructions.length}>
              <div className="tmw-label">{doneCount === m.instructions.length ? 'All handling instructions signed off' : `${m.instructions.length - doneCount} handling instruction${m.instructions.length - doneCount === 1 ? '' : 's'} pending`}</div>
              {m.instructions.map((s) => {
                const c = checks[s]; const isDone = !!(c && c.done);
                return (
                  <label className="tmw-check" key={s}>
                    <input type="checkbox" checked={isDone} disabled={saving === s} onChange={() => r.toggle(s)} />
                    <span>{s}{isDone && <span className="tmw-small"><br />Signed off by {c.by || 'dispatcher'}{c.at ? ` · ${shortDate(Date.parse(c.at))}` : ''}</span>}</span>
                  </label>
                );
              })}
            </div>
          )}
          <dl className="tmw-dl">
            <div><dt>Broker</dt><dd>{[m.broker, m.brokerPhone, m.brokerEmail].filter(Boolean).join(' · ') || NOT_REPORTED}</dd></div>
            {m.accessorials.length > 0 && <div><dt>Accessorials</dt><dd>{m.accessorials.join(' · ')}</dd></div>}
          </dl>
          {m.references.length > 0 && (
            <details className="tmw-group"><summary>References ({m.references.length})</summary>
              <dl className="tmw-dl">{m.references.map((x) => <div key={x}><dt className="numeric">{x}</dt><dd><button type="button" className="tmw-link" onClick={() => { try { navigator.clipboard.writeText(x); } catch { /* ignore */ } }}>Copy</button></dd></div>)}</dl>
            </details>
          )}
          {m.uncertain && <div className="tmw-conflict">Part of this document could not be structured — check the original before acting.</div>}
          <div className="tmw-source">AI-extracted · verify against the original.</div>
        </>
      )}
    </DropZone>
  );
}

function PacketPages({ trip }) {
  const pm = packetModel(trip);
  const [viewing, setViewing] = useState(null);
  if (!pm.count) return null;
  const metaFor = (pg) => (trip.docs || []).find((d) => String(d.id) === String(pg.docId)) || { id: pg.docId, filename: `Packet page ${pg.page}`, mediaType: 'application/pdf', page: pg.page };
  return (
    <div className="tmw-docs-block">
      <div className="tmw-label">Trip packet pages ({pm.count})</div>
      {pm.groups.map((g) => (
        <div key={g.type} className="tmw-packet-group">
          <div className="tmw-small"><b>{g.label}</b></div>
          {g.pages.map((pg) => (
            <div className="tmw-doc-version" key={`${pg.batchId}-${pg.file}-${pg.page}`}>
              <span className="tmw-small">Page {pg.page}{pg.file > 1 ? ` (file ${pg.file})` : ''} · {pg.type === 'driver_id' ? `${pg.driverName || 'Driver'} — ID on file` : (pg.summary || PAGE_TYPE_LABEL[pg.type])}{pg.matchedBy && pg.matchedBy !== 'manifest' ? ` · matched by ${pg.matchedBy}` : ''}</span>
              {pg.docId && <button type="button" className="tmw-btn tmw-btn-sm" onClick={() => setViewing(metaFor(pg))}>{pg.type === 'driver_id' ? 'View (restricted)' : 'View page'} ↗</button>}
            </div>
          ))}
          {g.pages.some((pg) => (pg.keyFields || []).length) && (
            <dl className="tmw-dl">{g.pages.flatMap((pg) => (pg.keyFields || []).map((k, i) => <div key={`${pg.page}-${i}`}><dt>{k.label} (p.{pg.page})</dt><dd>{k.value}</dd></div>))}</dl>
          )}
        </div>
      ))}
      {viewing && <DocViewer file={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function DocsBody({ docs, trip }) {
  const block = (title, d, empty) => (
    <div className="tmw-docs-block">
      <div className="tmw-label">{title}</div>
      {!d.groups.length && <div className="tmw-note">{d.originalMissing ? 'Extracted data exists, but the original file was not stored (uploaded before originals were kept).' : empty}</div>}
      {d.groups.map((g, i) => (
        <div className="tmw-doc-version" key={g.key}>
          <span className="tmw-small">{i === 0 ? 'Current' : 'Earlier'}{g.version ? ` · version ${g.version}` : ''} · {shortDate(Date.parse(g.uploadedAt))}{g.uploadedBy ? ` · ${g.uploadedBy}` : ''} · {g.files.length} file{g.files.length === 1 ? '' : 's'}</span>
          <DocButtons group={g} />
        </div>
      ))}
    </div>
  );
  const ts = tripSheetModel(trip);
  return (
    <div>
      {ts ? (
        <div className="tmw-docs-block">
          <div className="tmw-label">Trip sheet (manifest)</div>
          <div className="tmw-doc-version">
            <span className="tmw-small">Trip {trip.tripNumber} · {ts.pages} page{ts.pages === 1 ? '' : 's'}{ts.version ? ` · version ${ts.version}` : ''}{ts.uploadedAt ? ` · ${shortDate(Date.parse(ts.uploadedAt))}` : ''}{ts.uploadedBy ? ` · ${ts.uploadedBy}` : ''}</span>
            <span className="tmw-file-list"><TripSheetButton trip={trip} primary label="View trip sheet" /></span>
          </div>
        </div>
      ) : block('Trip sheet', docs.tripsheet, 'No trip sheet uploaded for this trip.')}
      {block('Rate confirmation', docs.ratecon, 'No rate confirmation uploaded for this trip.')}
      {docs.driverdoc && docs.driverdoc.groups.length > 0 && block('From the driver (POD / BOL)', docs.driverdoc, '')}
      {docs.artisdoc && docs.artisdoc.groups.length > 0 && block('Synergize documents (POD / BOL / invoice / rate con)', docs.artisdoc, '')}
      {docs.manifest && docs.manifest.groups.length > 0 && block('ARTIS trip manifests', docs.manifest, '')}
      <PacketPages trip={trip} />
      <div className="tmw-source">Originals are stored privately and open with your login.</div>
    </div>
  );
}

const SECTIONS = ['crew', 'driverapp', 'carrier', 'health', 'telematics', 'stops', 'ratecon', 'emails', 'docs'];
const TELEMATIC_GROUPS = ['Location & movement', 'Engine & fluids', 'Faults & emissions', 'Tires & brakes', 'Trailer & reefer', 'Maintenance & utilization'];

export default function TripCard({ trip, meta, glance, incidents, selected, onSelect, isOpen, setOpen, renderMap, onError, onChanged }) {
  const docs = documentsModel(trip);
  const conflicts = documentConflicts(trip);
  const crew = glance.crew;
  const health = healthModel(trip);
  const stops = stopsModel(trip);
  const r = useRateCon(trip, onError, onChanged);
  const oc = ocModel(trip);
  const late = lateFlag(incidents);
  const T = trip.tripNumber;
  const conflictCount = conflicts.length + crew.conflicts.length + ((trip.manifest && trip.manifest.diffs) || []).length + (trip.alerts || []).length;
  const allKeys = [...SECTIONS.map((x) => `${T}:${x}`), ...TELEMATIC_GROUPS.map((g) => `${T}:telematics:${g}`)];
  const allOpen = SECTIONS.filter((x) => x !== 'telematics').every((x) => isOpen(`${T}:${x}`));
  const toggleAll = () => allKeys.forEach((k) => setOpen(k, !allOpen));

  const wheel = crew.atWheel.verified ? crew.atWheel.name : null;
  const driverApp = useDriverApp(trip.tripNumber);
  const crewSum = `${crew.mode === 'Unverified' ? 'Unverified' : crew.mode} · ${wheel ? `${wheel} · Driving` : (crew.drivers.map((d) => `${d.name} · ${d.status}`).join(' / ') || 'No driver')}`;
  const stopTarget = stops.current ? `At stop ${stops.current.number ?? ''} · ${stops.current.customer || stops.current.place}` : (glance && glance.nextStopName ? `Next: ${glance.nextStopName}` : stops.next ? `Next: ${stops.next.customer || stops.next.place}` : '');
  const stopsSum = `${stops.tracked ? `${stops.completed} of ${stops.total} completed` : (stops.total ? `${stops.tmDeliveredCount} of ${stops.total} delivered (TruckMate)` : 'No stop list')}${stopTarget ? ` · ${stopTarget}` : ''}${stops.verified || !stops.total ? '' : ' · sequence unverified'}`;
  const rcMissing = !r.rc && !r.stage && isBrokerLoad(trip);   // broker load with no rate con → dispatch has to get it
  const rcSum = r.stage || (r.rc ? `Uploaded${r.rc.source ? ` from ${r.rc.source}` : ''}${r.m && r.m.load ? ` · Load ${r.m.load}` : ''}${r.m && r.m.instructions.length ? ` · Handling ${r.doneCount}/${r.m.instructions.length}` : ''}` : rcMissing ? '⚠ Missing — broker load: dispatch, upload it here (Jarvis also checks ARTIS every 2 h)' : 'Not uploaded');
  const pm = packetModel(trip);
  const docCount = [docs.ratecon, docs.tripsheet, docs.artisdoc, docs.manifest].reduce((n, k) => n + (k ? k.groups.reduce((m, g) => m + g.files.length, 0) : 0), 0);
  const docsSum = `${docCount} document${docCount === 1 ? '' : 's'}${pm.count ? ` · ${pm.count} packet page${pm.count === 1 ? '' : 's'}${pm.restricted ? ' (1+ restricted)' : ''}` : ''}${docs.ratecon.originalMissing || docs.tripsheet.originalMissing ? ' · some originals not stored' : ''}`;
  const pendingHandling = r.m && r.m.instructions.length - r.doneCount > 0;

  return (
    <section className="tmw-card" data-selected={selected} data-late={isDown(trip) ? 'late' : late ? late.level : undefined} aria-label={`Truck ${trip.powerUnit || 'unknown'}, trailer ${trip.trailer || 'unknown'}, trip ${trip.tripNumber}`}>
      <IdentityHeader trip={trip} selected={selected} isOpen={isOpen} setOpen={setOpen} oc={oc} late={late} stopped={glance.stopped} team={crew.mode === 'Team'} />
      <Overview trip={trip} meta={meta} glance={glance} incidents={incidents} health={health} conflicts={conflictCount}
        selected={selected} onSelect={onSelect} allOpen={allOpen} onToggleAll={toggleAll}
        mapOpen={isOpen(`${T}:map`)} onToggleMap={() => setOpen(`${T}:map`, !isOpen(`${T}:map`))} oc={oc} onChanged={onChanged} onOpenSection={(k) => setOpen(`${T}:${k}`, true)} />
      {isOpen(`${T}:map`) && renderMap && <div className="tmw-map-box" aria-label={`Live map for truck ${trip.powerUnit || ''}`}>{renderMap(trip)}</div>}
      <div className="tmw-accs">
        {oc ? (
          <Section id={`${T}:carrier`} label="Outside carrier" summary={`${oc.carrierName} · ${oc.crew === 'team' ? 'Team' : 'Solo'} · ${oc.lastLabel}${oc.overdue ? ' · update overdue' : ''}${oc.missing.length ? ` · missing ${oc.missing.join(', ')}` : ''}`} tone={oc.overdue || oc.missing.length ? 'warning' : null} isOpen={isOpen} setOpen={setOpen}>
            <CarrierBody trip={trip} oc={oc} onChanged={onChanged} onError={onError} />
          </Section>
        ) : (
          <>
            <Section id={`${T}:crew`} label="Driver crew" summary={crewSum} tone={crew.conflicts.length ? 'warning' : null} isOpen={isOpen} setOpen={setOpen}>
              <CrewBody trip={trip} onChanged={onChanged} onError={onError} />
            </Section>
            <Section id={`${T}:driverapp`} label="Driver app" summary={driverAppSummary(driverApp)} tone={driverAppTone(driverApp)} isOpen={isOpen} setOpen={setOpen}>
              <DriverAppBox trip={trip} onChanged={onChanged} onError={onError} />
            </Section>
            <Section id={`${T}:health`} label="Truck health" summary={health.faults.length ? `${health.faults.length} active fault${health.faults.length === 1 ? '' : 's'}` : health.status.label} tone={health.faults.length ? 'warning' : null} isOpen={isOpen} setOpen={setOpen}>
              <HealthBody trip={trip} health={health} isOpen={isOpen} setOpen={setOpen} />
            </Section>
          </>
        )}
        <Section id={`${T}:stops`} label="Outbound stops" summary={stopsSum} tone={stops.stops.some((x) => x.status === 'review') || (trip.manifest && (trip.manifest.diffs || []).length) ? 'warning' : null} isOpen={isOpen} setOpen={setOpen}>
          <StopsBody trip={trip} stops={stops} docs={docs} oc={oc} etas={glance && glance.stopEtas} onError={onError} onChanged={onChanged} />
        </Section>
        {trip.stopFlow && (
          <Section id={`${T}:driverstops`} label="Driver app stops" summary={driverStopsSummary(trip.stopFlow)} tone={trip.stopFlow.mismatches || trip.stopFlow.issues ? 'warning' : null} isOpen={isOpen} setOpen={setOpen}>
            <DriverStops sf={trip.stopFlow} />
          </Section>
        )}
        <Section id={`${T}:ratecon`} label="Rate confirmation" summary={rcSum} tone={pendingHandling || conflicts.length || rcMissing ? 'warning' : null} isOpen={isOpen} setOpen={setOpen}>
          {conflicts.length > 0 && <div className="tmw-conflict">Rate con ≠ trip sheet: {conflicts.map((c) => c.msg).join(' · ')}</div>}
          <RateConBody docs={docs} r={r} />
        </Section>
        <Section id={`${T}:emails`} label="Customer contacts & emails" summary={`${(trip.tasks || []).filter((t) => !t.done).length ? `📧 ${(trip.tasks || []).filter((t) => !t.done).length} to do · ` : ''}${trip.emails ? `${trip.emails.count} email${trip.emails.count === 1 ? '' : 's'}${trip.emails.open ? ` · ${trip.emails.open} need${trip.emails.open === 1 ? 's' : ''} a reply` : ''}` : 'No emails yet'}`} tone={(trip.emails && trip.emails.open) || (trip.tasks || []).some((t) => !t.done && t.urgency === 'urgent') ? 'warning' : null} isOpen={isOpen} setOpen={setOpen}>
          {isOpen(`${T}:emails`) && <Suspense fallback={<div className="tmw-note">Loading…</div>}><LoadTasks trip={T} tasks={trip.tasks} onChanged={onChanged} /><StatusMail trip={T} /><EmailBox trip={T} onChanged={onChanged} /></Suspense>}
        </Section>
        <Section id={`${T}:docs`} label="Trip documents" summary={docsSum} isOpen={isOpen} setOpen={setOpen}>
          <DocsBody docs={docs} trip={trip} />
        </Section>
      </div>
    </section>
  );
}
